import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Spinner, Alert, Modal, Toast, ToastContainer } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  GET_ADMIN_ORDERS,
  GET_ADMIN_CONTACTS,
  GET_ADMIN_REVIEWS,
  GET_ADMIN_QUOTES,
  GET_ADMIN_USERS,
  GET_PRODUCTS,
  UPDATE_ORDER_STATUS,
  UPDATE_ORDER,
  MARK_CONTACT_READ,
  APPROVE_REVIEW,
  UPDATE_QUOTE_STATUS,
  CREATE_USER,
  UPDATE_USER,
  DELETE_USER,
  UPDATE_PRODUCT_PRICING,
  UPDATE_PRODUCT,
  GET_ADMIN_JOBCARDS,
  CREATE_JOBCARD,
  UPDATE_JOBCARD,
  DELETE_JOBCARD,
  GENERATE_INVOICE_FROM_JOBCARD,
  GET_INVENTORY_ITEMS,
  CREATE_INVENTORY_ITEM,
  UPDATE_INVENTORY_ITEM,
  DELETE_INVENTORY_ITEM,
  ADMIN_CREATE_ORDER,
  DELETE_ORDER,
  DELETE_CONTACT,
  DELETE_REVIEW,
  DELETE_QUOTE,
  GET_STAFF_ATTENDANCE,
  GET_ATTENDANCE_SUMMARY,
  GET_ALL_STAFF_ATTENDANCE_SUMMARY,
  MARK_ATTENDANCE,
  DELETE_PRODUCT,
  GET_PAYSLIPS,
  DELETE_PAYSLIP,
  UPDATE_PAYSLIP_STATUS,
} from '../graphql/operations';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Footer from '../components/Footer';
import AdminDashboardHeader from '../components/admin/AdminDashboardHeader';
import AdminTabNavigation, { TabKey } from '../components/admin/AdminTabNavigation';
import OrderDetailsModal from '../components/admin/OrderDetailsModal';
import UserModal from '../components/admin/UserModal';
import PricingModal from '../components/admin/PricingModal';
import JobCardModal from '../components/admin/JobCardModal';
import JobTicketModal from '../components/admin/JobTicketModal';
import CreateOrderModal from '../components/admin/CreateOrderModal';
import CreateQuoteModal from '../components/admin/CreateQuoteModal';
import CreateEnquiryModal from '../components/admin/CreateEnquiryModal';
import ProductModal from '../components/admin/ProductModal';
import PayslipModal from '../components/admin/PayslipModal';
import EstimationGenerator from '../components/admin/EstimationGenerator';
import InvoiceGenerator from '../components/admin/InvoiceGenerator';
import CustomerModal from '../components/admin/CustomerModal';
import SalesTracker from '../components/admin/SalesTracker';
import ExpenseTracker from '../components/admin/ExpenseTracker';
import PurchaseTracker from '../components/admin/PurchaseTracker';
import TaskManager from '../components/admin/TaskManager';
import BankStatementAnalyzer from '../components/admin/BankStatementAnalyzer';
import { formatDesignFileName } from '../components/AppNavbar';
import AdminPagination from '../components/admin/AdminPagination';

const isOutOfStockItem = (item: any) => {
  if (item.category === 'Banner Printing') {
    return (item.overallLength || 0) <= (item.rollLengthLimit || 0);
  }
  return item.stock === 0;
};

const isLowStockItem = (item: any) => {
  if (item.category === 'Banner Printing') {
    const length = item.overallLength || 0;
    const limit = item.rollLengthLimit || 0;
    return length > limit && length <= limit + (item.minStock || 0);
  }
  return item.stock > 0 && item.stock <= item.minStock;
};

const isInStockItem = (item: any) => {
  if (item.category === 'Banner Printing') {
    const length = item.overallLength || 0;
    const limit = item.rollLengthLimit || 0;
    return length > limit + (item.minStock || 0);
  }
  return item.stock > item.minStock;
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    const role = localStorage.getItem('role');
    return role === 'manager' ? 'attendance' : 'orders';
  });

  // Orders search & filtering state
  const [orderSearch, setOrderSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [showCreateQuoteModal, setShowCreateQuoteModal] = useState(false);
  const [showCreateEnquiryModal, setShowCreateEnquiryModal] = useState(false);

  // Selected order details state
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isEditOrderMode, setIsEditOrderMode] = useState(false);
  const [updatingOrderStatusId, setUpdatingOrderStatusId] = useState<string | null>(null);

  // Users management state
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [userForm, setUserForm] = useState({ username: '', password: '', role: 'customer' });
  const [userFormError, setUserFormError] = useState('');

  // Customers management state
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);

  // Product pricing state
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [pricingForm, setPricingForm] = useState<{ basePrice: number; options: any[] }>({ basePrice: 0, options: [] });
  const [pricingError, setPricingError] = useState('');

  // Product handling state
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [productSearch, setProductSearch] = useState('');

  // Job Cards state
  const [showJobCardModal, setShowJobCardModal] = useState(false);
  const [isEditingJobCard, setIsEditingJobCard] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState<any>(null);
  const [jobCardForm, setJobCardForm] = useState({
    operator: '',
    designer: '',
    machine: '',
    material: '',
    finishingInstructions: '',
    priority: 'medium',
    notes: '',
  });
  const [jobCardError, setJobCardError] = useState('');
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [jobCardViewMode, setJobCardViewMode] = useState<'kanban' | 'table'>('kanban');
  const [jobCardSearch, setJobCardSearch] = useState('');
  const [jobCardStatusFilter, setJobCardStatusFilter] = useState('all');

  // Inventory management state
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryCategoryFilter, setInventoryCategoryFilter] = useState('all');
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<any>(null);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [isEditingInventory, setIsEditingInventory] = useState(false);
  const [inventoryForm, setInventoryForm] = useState({
    name: '',
    category: 'Banner Printing',
    stock: 0,
    unit: 'pcs',
    minStock: 10,
    supplier: '',
    notes: '',
    orderDate: '',
    bannerType: '',
    bannerSize: '',
    overallLength: 0,
    rollLengthLimit: 10,
  });
  const [inventoryFormError, setInventoryFormError] = useState('');

  // Attendance state
  const [attendanceDate, setAttendanceDate] = useState(() => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const localToday = new Date(today.getTime() - (offset * 60 * 1000));
    return localToday.toISOString().slice(0, 10);
  });
  const [selectedAttendanceUser, setSelectedAttendanceUser] = useState<any>(null);
  const [showAttendanceSummaryModal, setShowAttendanceSummaryModal] = useState(false);
  const [attendanceSummaryStart, setAttendanceSummaryStart] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [attendanceSummaryEnd, setAttendanceSummaryEnd] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  });
  const [attendanceViewMode, setAttendanceViewMode] = useState<'daily' | 'all-employees'>('daily');
  const [allEmployeesAttendanceStart, setAllEmployeesAttendanceStart] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [allEmployeesAttendanceEnd, setAllEmployeesAttendanceEnd] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  });

  // Payslips state
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [payslipSearch, setPayslipSearch] = useState('');
  const [payslipMonthFilter, setPayslipMonthFilter] = useState<string>('all');
  const [payslipYearFilter, setPayslipYearFilter] = useState<string>('all');
  const [payslipEmployeeFilter, setPayslipEmployeeFilter] = useState<string>('all');

  // Toast notifications state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'danger' | 'warning' | 'info'>('success');

  // Pagination states
  const ITEMS_PER_PAGE = 10;
  const [ordersPage, setOrdersPage] = useState(1);
  const [contactsPage, setContactsPage] = useState(1);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [quotesPage, setQuotesPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [customersPage, setCustomersPage] = useState(1);
  const [productsPage, setProductsPage] = useState(1);
  const [jobCardsPage, setJobCardsPage] = useState(1);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [attendanceDailyPage, setAttendanceDailyPage] = useState(1);
  const [attendanceSummaryPage, setAttendanceSummaryPage] = useState(1);
  const [payslipsPage, setPayslipsPage] = useState(1);

  const triggerToast = (message: string, variant: 'success' | 'danger' | 'warning' | 'info' = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };


  // Authentication check
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token || (role !== 'admin' && role !== 'manager' && role !== 'admin-manager')) {
      navigate('/admin-login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/admin-login');
  };

  // Queries
  const {
    data: ordersData,
    loading: ordersLoading,
    refetch: refetchOrders
  } = useQuery(GET_ADMIN_ORDERS, {
    fetchPolicy: 'network-only',
  });

  const {
    data: contactsData,
    loading: contactsLoading,
    refetch: refetchContacts
  } = useQuery(GET_ADMIN_CONTACTS, {
    fetchPolicy: 'network-only',
  });

  const {
    data: reviewsData,
    loading: reviewsLoading,
    refetch: refetchReviews
  } = useQuery(GET_ADMIN_REVIEWS, {
    fetchPolicy: 'network-only',
  });

  const {
    data: quotesData,
    loading: quotesLoading,
    refetch: refetchQuotes
  } = useQuery(GET_ADMIN_QUOTES, {
    fetchPolicy: 'network-only',
  });

  const {
    data: usersData,
    loading: usersLoading,
    refetch: refetchUsers
  } = useQuery(GET_ADMIN_USERS, {
    fetchPolicy: 'network-only',
  });

  const {
    data: rawProductsData,
    loading: productsLoading,
    refetch: refetchProducts
  } = useQuery(GET_PRODUCTS, {
    variables: { includeInactive: true },
    fetchPolicy: 'network-only',
  });

  const {
    data: jobCardsData,
    loading: jobCardsLoading,
    refetch: refetchJobCards
  } = useQuery(GET_ADMIN_JOBCARDS, {
    fetchPolicy: 'network-only',
  });

  const {
    data: inventoryData,
    loading: inventoryLoading,
    refetch: refetchInventory
  } = useQuery(GET_INVENTORY_ITEMS, {
    fetchPolicy: 'network-only',
  });

  const {
    data: staffAttendanceData,
    loading: staffAttendanceLoading,
    refetch: refetchStaffAttendance,
  } = useQuery(GET_STAFF_ATTENDANCE, {
    variables: { date: attendanceDate },
    fetchPolicy: 'network-only',
  });

  const {
    data: summaryData,
    loading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery(GET_ATTENDANCE_SUMMARY, {
    variables: {
      userId: selectedAttendanceUser?.id || '',
      startDate: attendanceSummaryStart,
      endDate: attendanceSummaryEnd,
    },
    skip: !selectedAttendanceUser,
    fetchPolicy: 'network-only',
  });

  const {
    data: allStaffSummaryData,
    loading: allStaffSummaryLoading,
    refetch: refetchAllStaffSummary,
  } = useQuery(GET_ALL_STAFF_ATTENDANCE_SUMMARY, {
    variables: {
      startDate: allEmployeesAttendanceStart,
      endDate: allEmployeesAttendanceEnd,
    },
    fetchPolicy: 'network-only',
    skip: activeTab !== 'attendance' || attendanceViewMode !== 'all-employees',
  });

  const {
    data: payslipsData,
    loading: payslipsLoading,
    refetch: refetchPayslips,
  } = useQuery(GET_PAYSLIPS, {
    fetchPolicy: 'network-only',
  });

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const filteredPayslips = (payslipsData?.payslips || []).filter((p: any) => {
    if (payslipSearch) {
      const query = payslipSearch.toLowerCase();
      const username = p.user?.username?.toLowerCase() || '';
      if (!username.includes(query)) return false;
    }
    if (payslipMonthFilter !== 'all') {
      if (p.month !== parseInt(payslipMonthFilter)) return false;
    }
    if (payslipYearFilter !== 'all') {
      if (p.year !== parseInt(payslipYearFilter)) return false;
    }
    return true;
  });

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchOrders(),
        refetchContacts(),
        refetchReviews(),
        refetchQuotes(),
        refetchUsers(),
        refetchProducts(),
        refetchJobCards(),
        refetchInventory ? refetchInventory() : Promise.resolve(),
        refetchStaffAttendance ? refetchStaffAttendance() : Promise.resolve(),
        refetchPayslips ? refetchPayslips() : Promise.resolve(),
      ]);
    } catch (err) {
      console.error('Error refreshing dashboard:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Mutations
  const [updateOrderStatusMutation] = useMutation(UPDATE_ORDER_STATUS, {
    onCompleted: () => refetchOrders()
  });

  const [updateOrderMutation] = useMutation(UPDATE_ORDER, {
    onCompleted: () => refetchOrders()
  });

  const [markContactReadMutation] = useMutation(MARK_CONTACT_READ, {
    onCompleted: () => refetchContacts()
  });

  const [approveReviewMutation] = useMutation(APPROVE_REVIEW, {
    onCompleted: () => refetchReviews()
  });

  const [updateQuoteStatusMutation] = useMutation(UPDATE_QUOTE_STATUS, {
    onCompleted: () => refetchQuotes()
  });

  const [createUserMutation] = useMutation(CREATE_USER, {
    refetchQueries: [{ query: GET_ADMIN_USERS }],
    onCompleted: () => refetchUsers()
  });

  const [updateUserMutation] = useMutation(UPDATE_USER, {
    refetchQueries: [{ query: GET_ADMIN_USERS }],
    onCompleted: () => refetchUsers()
  });

  const [deleteUserMutation] = useMutation(DELETE_USER, {
    refetchQueries: [{ query: GET_ADMIN_USERS }],
    onCompleted: () => refetchUsers()
  });

  const [updateProductPricingMutation] = useMutation(UPDATE_PRODUCT_PRICING, {
    refetchQueries: [{ query: GET_PRODUCTS, variables: { includeInactive: true } }],
    onCompleted: () => { if (refetchProducts) refetchProducts(); }
  });

  const [deleteProductMutation] = useMutation(DELETE_PRODUCT, {
    refetchQueries: [{ query: GET_PRODUCTS, variables: { includeInactive: true } }],
    onCompleted: () => { if (refetchProducts) refetchProducts(); }
  });

  const [updateProductMutation] = useMutation(UPDATE_PRODUCT, {
    refetchQueries: [{ query: GET_PRODUCTS, variables: { includeInactive: true } }],
    onCompleted: () => { if (refetchProducts) refetchProducts(); }
  });

  const [createJobCardMutation] = useMutation(CREATE_JOBCARD, {
    refetchQueries: [{ query: GET_ADMIN_JOBCARDS }, { query: GET_ADMIN_ORDERS }],
    onCompleted: () => {
      refetchJobCards();
      refetchOrders();
    }
  });

  const [updateJobCardMutation] = useMutation(UPDATE_JOBCARD, {
    refetchQueries: [{ query: GET_ADMIN_JOBCARDS }],
    onCompleted: () => refetchJobCards()
  });

  const [deleteJobCardMutation] = useMutation(DELETE_JOBCARD, {
    refetchQueries: [{ query: GET_ADMIN_JOBCARDS }, { query: GET_ADMIN_ORDERS }],
    onCompleted: () => {
      refetchJobCards();
      refetchOrders();
    }
  });

  const [generateInvoiceFromJobCard] = useMutation(GENERATE_INVOICE_FROM_JOBCARD);

  const [createInventoryItemMutation] = useMutation(CREATE_INVENTORY_ITEM, {
    onCompleted: () => { if (refetchInventory) refetchInventory(); }
  });

  const [updateInventoryItemMutation] = useMutation(UPDATE_INVENTORY_ITEM, {
    onCompleted: () => { if (refetchInventory) refetchInventory(); }
  });

  const [deleteInventoryItemMutation] = useMutation(DELETE_INVENTORY_ITEM, {
    onCompleted: () => { if (refetchInventory) refetchInventory(); }
  });

  const [deleteOrderMutation] = useMutation(DELETE_ORDER, {
    onCompleted: () => refetchOrders()
  });

  const [deleteContactMutation] = useMutation(DELETE_CONTACT, {
    onCompleted: () => refetchContacts()
  });

  const [deleteReviewMutation] = useMutation(DELETE_REVIEW, {
    onCompleted: () => refetchReviews()
  });

  const [deleteQuoteMutation] = useMutation(DELETE_QUOTE, {
    onCompleted: () => refetchQuotes()
  });

  const [markAttendanceMutation] = useMutation(MARK_ATTENDANCE, {
    onCompleted: () => refetchStaffAttendance()
  });

  const [deletePayslipMutation] = useMutation(DELETE_PAYSLIP, {
    onCompleted: () => refetchPayslips()
  });

  const [updatePayslipStatusMutation] = useMutation(UPDATE_PAYSLIP_STATUS, {
    onCompleted: () => refetchPayslips()
  });

  const handleMarkAttendance = async (userId: string, date: string, status: string, checkIn: string, checkOut: string, notes: string) => {
    try {
      const { data } = await markAttendanceMutation({
        variables: { userId, date, status, checkIn, checkOut, notes }
      });
      if (data?.markAttendance?.success) {
        triggerToast('Attendance updated successfully.', 'success');
        if (selectedAttendanceUser && selectedAttendanceUser.id === userId) {
          refetchSummary();
        }
      } else {
        triggerToast(data?.markAttendance?.message || 'Failed to update attendance.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update attendance.', 'danger');
    }
  };

  const handleMarkAllPresent = async () => {
    const list = staffAttendanceData?.staffAttendance || [];
    if (list.length === 0) return;

    if (!window.confirm(`Are you sure you want to mark all ${list.length} staff members as Present for ${attendanceDate}?`)) {
      return;
    }

    try {
      let successCount = 0;
      for (const item of list) {
        const { data } = await markAttendanceMutation({
          variables: {
            userId: item.user.id,
            date: attendanceDate,
            status: 'present',
            checkIn: item.attendance?.checkIn || '09:00',
            checkOut: item.attendance?.checkOut || '18:00',
            notes: item.attendance?.notes || '',
          }
        });
        if (data?.markAttendance?.success) {
          successCount++;
        }
      }
      triggerToast(`Successfully marked ${successCount} staff members as Present.`, 'success');
      refetchStaffAttendance();
    } catch (err: any) {
      triggerToast(err.message || 'Failed to mark all as present.', 'danger');
    }
  };

  // Track which item is pending delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Track confirmation states for save operations
  const [confirmSaveUserId, setConfirmSaveUserId] = useState<string | null>(null);
  const [confirmSaveJobCardId, setConfirmSaveJobCardId] = useState<string | null>(null);

  // Event handlers
  const handleUpdateStatus = async (orderId: string, newStatus: string) => {
    setUpdatingOrderStatusId(orderId);
    try {
      const { data } = await updateOrderStatusMutation({
        variables: { orderId, status: newStatus }
      });
      if (data?.updateOrderStatus?.success) {
        triggerToast(data.updateOrderStatus.message || `Order status updated to "${newStatus}"`, 'success');
      } else {
        triggerToast(data?.updateOrderStatus?.message || 'Failed to update order status.', 'danger');
      }
      // Sync local modal state if open
      if (selectedOrder && selectedOrder.orderId === orderId) {
        setSelectedOrder((prev: any) => ({ ...prev, status: newStatus }));
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update order status.', 'danger');
    } finally {
      setUpdatingOrderStatusId(null);
    }
  };

  const handleUpdateOrder = async (input: any) => {
    setUpdatingOrderStatusId(input.orderId);
    try {
      const { data } = await updateOrderMutation({
        variables: { input }
      });
      if (data?.updateOrder?.success) {
        triggerToast(data.updateOrder.message || 'Order updated successfully', 'success');
        if (selectedOrder && selectedOrder.orderId === input.orderId) {
          setSelectedOrder((prev: any) => ({ ...prev, ...input }));
        }
        setShowOrderModal(false);
        setIsEditOrderMode(false);
      } else {
        triggerToast(data?.updateOrder?.message || 'Failed to update order.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update order.', 'danger');
    } finally {
      setUpdatingOrderStatusId(null);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      const { data } = await markContactReadMutation({ variables: { id } });
      if (data?.markContactRead?.success) {
        triggerToast('Enquiry marked as read.', 'success');
      } else {
        triggerToast(data?.markContactRead?.message || 'Failed to update enquiry status.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update enquiry status.', 'danger');
    }
  };

  const handleApproveReview = async (id: string) => {
    try {
      const { data } = await approveReviewMutation({ variables: { id } });
      if (data?.approveReview?.success) {
        triggerToast('Review approved and published.', 'success');
      } else {
        triggerToast(data?.approveReview?.message || 'Failed to approve review.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to approve review.', 'danger');
    }
  };

  const [updatingQuoteId, setUpdatingQuoteId] = useState<string | null>(null);

  const handleUpdateQuoteStatus = async (quoteId: string, newStatus: string) => {
    setUpdatingQuoteId(quoteId);
    try {
      const { data } = await updateQuoteStatusMutation({
        variables: { quoteId, status: newStatus }
      });
      if (data?.updateQuoteStatus?.success) {
        triggerToast(`Quote status updated to "${newStatus}"`, 'success');
      } else {
        triggerToast(data?.updateQuoteStatus?.message || 'Failed to update quote status.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update quote status.', 'danger');
    } finally {
      setUpdatingQuoteId(null);
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    if (confirmDeleteId !== orderId) { setConfirmDeleteId(orderId); return; }
    setConfirmDeleteId(null);
    try {
      await deleteOrderMutation({ variables: { orderId } });
      triggerToast('Order deleted.', 'success');
    } catch { triggerToast('Failed to delete order.', 'danger'); }
  };

  const handleDeleteContact = async (id: string) => {
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    setConfirmDeleteId(null);
    try {
      await deleteContactMutation({ variables: { id } });
      triggerToast('Enquiry deleted.', 'success');
    } catch { triggerToast('Failed to delete enquiry.', 'danger'); }
  };

  const handleDeleteReview = async (id: string) => {
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    setConfirmDeleteId(null);
    try {
      await deleteReviewMutation({ variables: { id } });
      triggerToast('Review deleted.', 'success');
    } catch { triggerToast('Failed to delete review.', 'danger'); }
  };

  const handleDeleteQuote = async (quoteId: string) => {
    if (confirmDeleteId !== quoteId) { setConfirmDeleteId(quoteId); return; }
    setConfirmDeleteId(null);
    try {
      await deleteQuoteMutation({ variables: { quoteId } });
      triggerToast('Quote deleted.', 'success');
    } catch { triggerToast('Failed to delete quote.', 'danger'); }
  };

  const handleDeleteProduct = async (id: string) => {
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    setConfirmDeleteId(null);
    try {
      const { data } = await deleteProductMutation({
        variables: { id }
      });
      if (data?.deleteProduct?.success) {
        triggerToast(data.deleteProduct.message || 'Product deleted.', 'success');
        refetchProducts();
      } else {
        triggerToast(data?.deleteProduct?.message || 'Failed to delete product.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Error deleting product.', 'danger');
    }
  };

  const handleDeletePayslip = async (id: string) => {
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    setConfirmDeleteId(null);
    try {
      const { data } = await deletePayslipMutation({ variables: { id } });
      if (data?.deletePayslip?.success) {
        triggerToast('Payslip deleted.', 'success');
      } else {
        triggerToast(data?.deletePayslip?.message || 'Failed to delete payslip.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to delete payslip.', 'danger');
    }
  };

  const handleTogglePayslipStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'Paid' ? 'Pending' : 'Paid';
    try {
      const { data } = await updatePayslipStatusMutation({ variables: { id, status: newStatus } });
      if (data?.updatePayslipStatus?.success) {
        triggerToast(`Payslip status updated to ${newStatus}.`, 'success');
      } else {
        triggerToast(data?.updatePayslipStatus?.message || 'Failed to update status.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update status.', 'danger');
    }
  };

  const numberToWords = (num: number): string => {
    const a = [
      '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
    ];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (num === 0) return 'Zero';

    const convertLessThanOneThousand = (n: number): string => {
      if (n === 0) return '';
      let str = '';
      if (n >= 100) {
        str += a[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        str += b[Math.floor(n / 10)] + ' ';
        n %= 10;
      }
      if (n > 0) {
        str += a[n] + ' ';
      }
      return str.trim();
    };

    let str = '';
    let temp = Math.floor(num); // Ensure integer evaluation

    if (Math.floor(temp / 10000000) > 0) {
      str += convertLessThanOneThousand(Math.floor(temp / 10000000)) + ' Crore ';
      temp %= 10000000;
    }
    if (Math.floor(temp / 100000) > 0) {
      str += convertLessThanOneThousand(Math.floor(temp / 100000)) + ' Lakh ';
      temp %= 100000;
    }
    if (Math.floor(temp / 1000) > 0) {
      str += convertLessThanOneThousand(Math.floor(temp / 1000)) + ' Thousand ';
      temp %= 1000;
    }
    if (temp > 0) {
      str += convertLessThanOneThousand(temp);
    }

    return str.trim() + ' Rupees Only';
  };

  const downloadPayslipPDF = (payslip: any) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const primaryColor = [26, 54, 93];
    const secondaryColor = [74, 85, 104];
    const lightBg = [247, 250, 252];

    // Header Panel
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('PRATHITHA DIGITAL PRINTERS', 15, 18);

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('No.42, J.V.R Nagar Complex, N.S.R Road, Chromepet, Chennai - 600044', 15, 26);
    doc.text('Phone: +91 91508 08130 | Email: pdp@prathithadp.com', 15, 31);

    doc.setFontSize(16);
    doc.setFont('Helvetica', 'bold');
    doc.text('PAYSLIP', 160, 24, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont('Helvetica', 'normal');
    const monthNames = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    doc.text(`${monthNames[payslip.month]} ${payslip.year}`, 160, 30, { align: 'right' });

    let y = 50;

    doc.setFontSize(11);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('EMPLOYEE DETAILS', 15, y);
    doc.line(15, y + 2, 195, y + 2);
    y += 8;

    doc.setFontSize(9);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);

    doc.text('Employee Name:', 15, y);
    doc.setFont('Helvetica', 'normal');
    doc.text(payslip.user?.username || 'N/A', 50, y);

    doc.setFont('Helvetica', 'bold');
    doc.text('Designation:', 15, y + 6);
    doc.setFont('Helvetica', 'normal');
    doc.text(payslip.user?.role || 'Staff', 50, y + 6);

    doc.setFont('Helvetica', 'bold');
    doc.text('Working Days:', 115, y);
    doc.setFont('Helvetica', 'normal');
    doc.text(String(payslip.workingDays || 30), 150, y);

    doc.setFont('Helvetica', 'bold');
    doc.text('Present Days:', 115, y + 6);
    doc.setFont('Helvetica', 'normal');
    doc.text(String(payslip.presentDays || 0), 150, y + 6);

    y += 18;

    doc.setFontSize(11);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('SALARY BREAKDOWN', 15, y);
    doc.line(15, y + 2, 195, y + 2);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [['Earnings', 'Amount (INR)', 'Deductions', 'Amount (INR)']],
      body: [
        [
          'Basic Salary',
          `Rs. ${payslip.baseSalary.toLocaleString('en-IN')}`,
          'Loss of Pay / Unpaid Leaves',
          `Rs. ${payslip.deductions.toLocaleString('en-IN')}`
        ],
        [
          'Allowances & Bonuses',
          `Rs. ${payslip.allowances.toLocaleString('en-IN')}`,
          'Income Tax / PF',
          `Rs. ${payslip.tax.toLocaleString('en-IN')}`
        ]
      ],
      theme: 'striped',
      headStyles: {
        fillColor: [26, 54, 93],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [51, 51, 51]
      },
      columnStyles: {
        0: { fontStyle: 'bold' },
        2: { fontStyle: 'bold' }
      },
      margin: { left: 15, right: 15 }
    });

    y = (doc as any).lastAutoTable.finalY + 10;

    doc.setFillColor(lightBg[0], lightBg[1], lightBg[2]);
    doc.rect(15, y, 180, 24, 'F');
    doc.rect(15, y, 180, 24, 'S');

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);

    const grossEarnings = payslip.baseSalary + payslip.allowances;
    const totalDeductions = payslip.deductions + payslip.tax;

    doc.text(`Gross Earnings: Rs. ${grossEarnings.toLocaleString('en-IN')}`, 20, y + 6);
    doc.text(`Total Deductions: Rs. ${totalDeductions.toLocaleString('en-IN')}`, 20, y + 12);
    doc.text(`Payment Mode: ${payslip.paymentMode || 'Bank Transfer'}`, 20, y + 18);

    doc.setFontSize(12);
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text(`Net Salary: Rs. ${payslip.netSalary.toLocaleString('en-IN')}`, 115, y + 8);

    doc.setFontSize(9);
    doc.setFont('Helvetica', 'bold');
    const statusText = payslip.status === 'Paid' ? 'PAID' : 'PENDING PAYMENT';
    doc.setTextColor(payslip.status === 'Paid' ? 34 : 217, payslip.status === 'Paid' ? 139 : 83, payslip.status === 'Paid' ? 34 : 79);
    doc.text(`Status: ${statusText}`, 115, y + 14);

    y += 32;

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Amount in Words:', 15, y);
    doc.setFont('Helvetica', 'italic');
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.text(numberToWords(payslip.netSalary), 48, y);

    y += 12;

    if (payslip.notes) {
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('Notes / Remarks:', 15, y);
      doc.setFont('Helvetica', 'normal');
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.text(payslip.notes, 15, y + 5);
      y += 18;
    }

    y += 15;

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);

    doc.line(15, y + 10, 75, y + 10);
    doc.text('Employer Signature', 15, y + 14);

    doc.line(135, y + 10, 195, y + 10);
    doc.text('Employee Signature', 135, y + 14);

    doc.setFontSize(7.5);
    doc.text('Generated electronically by Prathitha Payroll Management System.', 15, 280);

    doc.save(`Payslip_${payslip.user?.username || 'Employee'}_${monthNames[payslip.month]}_${payslip.year}.pdf`);
  };

  const handleOpenAddUser = () => {
    setIsEditingUser(false);
    setSelectedUser(null);
    setUserForm({ username: '', password: '', role: 'customer' });
    setUserFormError('');
    setShowUserModal(true);
  };

  const handleOpenEditUser = (user: any) => {
    setIsEditingUser(true);
    setSelectedUser(user);
    setUserForm({ username: user.username, password: '', role: user.role });
    setUserFormError('');
    setShowUserModal(true);
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();

    const userId = isEditingUser ? selectedUser.id : 'new';

    // First click - show confirmation
    if (confirmSaveUserId !== userId) {
      setConfirmSaveUserId(userId);
      return;
    }

    // Reset confirmation state
    setConfirmSaveUserId(null);
    setUserFormError('');

    if (!userForm.username.trim()) {
      setUserFormError('Username is required.');
      return;
    }

    if (!isEditingUser && !userForm.password.trim()) {
      setUserFormError('Password is required.');
      return;
    }

    try {
      if (isEditingUser) {
        const { data } = await updateUserMutation({
          variables: {
            id: selectedUser.id,
            username: userForm.username.trim(),
            password: userForm.password ? userForm.password : undefined,
            role: userForm.role,
          }
        });
        if (data?.updateUser?.success) {
          setShowUserModal(false);
          triggerToast('User updated successfully!', 'success');
          // If editing self, update localStorage to reflect changes immediately
          const activeUsername = localStorage.getItem('username');
          if (selectedUser.username.toLowerCase() === activeUsername?.toLowerCase()) {
            if (data.updateUser.user.username) {
              localStorage.setItem('username', data.updateUser.user.username);
            }
            if (data.updateUser.user.role) {
              localStorage.setItem('role', data.updateUser.user.role);
            }
            window.location.reload();
          }
        } else {
          setUserFormError(data?.updateUser?.message || 'Failed to update user.');
          triggerToast(data?.updateUser?.message || 'Failed to update user.', 'danger');
        }
      } else {
        const { data } = await createUserMutation({
          variables: {
            username: userForm.username.trim(),
            password: userForm.password,
            role: userForm.role,
          }
        });
        if (data?.createUser?.success) {
          setShowUserModal(false);
          triggerToast('User created successfully!', 'success');
        } else {
          setUserFormError(data?.createUser?.message || 'Failed to create user.');
          triggerToast(data?.createUser?.message || 'Failed to create user.', 'danger');
        }
      }
    } catch (err: any) {
      setUserFormError(err.message || 'Operation failed.');
      triggerToast(err.message || 'Operation failed.', 'danger');
    }
  };

  const handleDeleteUser = async (user: any) => {
    const activeUsername = localStorage.getItem('username');
    if (user.username.toLowerCase() === activeUsername?.toLowerCase()) {
      triggerToast('You cannot delete your own logged-in account.', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      return;
    }

    try {
      const { data } = await deleteUserMutation({
        variables: { id: user.id }
      });
      if (data?.deleteUser?.success) {
        triggerToast('User deleted successfully!', 'success');
      } else {
        triggerToast(data?.deleteUser?.message || 'Failed to delete user.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to delete user.', 'danger');
    }
  };

  const handleOpenAddCustomer = () => {
    setIsEditingCustomer(false);
    setSelectedCustomer(null);
    setShowCustomerModal(true);
  };

  const handleOpenEditCustomer = (customer: any) => {
    setIsEditingCustomer(true);
    setSelectedCustomer(customer);
    setShowCustomerModal(true);
  };

  const handleDeleteCustomer = async (customer: any) => {
    if (!window.confirm(`Are you sure you want to delete customer "${customer.username}"?`)) {
      return;
    }
    try {
      const { data } = await deleteUserMutation({
        variables: { id: customer.id }
      });
      if (data?.deleteUser?.success) {
        triggerToast('Customer profile deleted successfully!', 'success');
      } else {
        triggerToast(data?.deleteUser?.message || 'Failed to delete customer.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to delete customer.', 'danger');
    }
  };

  // Product pricing handlers
  const handleOpenPricingModal = (product: any) => {
    setSelectedProduct(product);
    const optionsClone = product.options.map((opt: any) => ({
      name: opt.name,
      label: opt.label,
      type: opt.type,
      choices: opt.choices ? opt.choices.map((c: any) => ({
        label: c.label,
        extraCost: c.extraCost,
      })) : [],
      min: opt.min,
      max: opt.max,
      step: opt.step,
      unit: opt.unit,
    }));
    setPricingForm({
      basePrice: product.basePrice,
      options: optionsClone,
    });
    setPricingError('');
    setShowPricingModal(true);
  };

  const handleUpdateOptionChoiceCost = (optIndex: number, choiceIndex: number, newCost: number) => {
    setPricingForm(prev => {
      const updatedOptions = [...prev.options];
      updatedOptions[optIndex] = {
        ...updatedOptions[optIndex],
        choices: updatedOptions[optIndex].choices.map((choice: any, idx: number) =>
          idx === choiceIndex ? { ...choice, extraCost: newCost } : choice
        )
      };
      return { ...prev, options: updatedOptions };
    });
  };

  const handleAddOptionChoice = (optIndex: number, label: string, extraCost: number) => {
    if (!label.trim()) return;
    setPricingForm(prev => {
      const updatedOptions = [...prev.options];
      updatedOptions[optIndex] = {
        ...updatedOptions[optIndex],
        choices: [...(updatedOptions[optIndex].choices || []), { label: label.trim(), extraCost }]
      };
      return { ...prev, options: updatedOptions };
    });
  };

  const handleDeleteOptionChoice = (optIndex: number, choiceIndex: number) => {
    setPricingForm(prev => {
      const updatedOptions = [...prev.options];
      updatedOptions[optIndex] = {
        ...updatedOptions[optIndex],
        choices: updatedOptions[optIndex].choices.filter((_: any, idx: number) => idx !== choiceIndex)
      };
      return { ...prev, options: updatedOptions };
    });
  };

  const handleSavePricing = async (e: React.FormEvent) => {
    e.preventDefault();
    setPricingError('');
    try {
      const cleanOptions = pricingForm.options.map(opt => ({
        name: opt.name,
        label: opt.label,
        type: opt.type,
        choices: opt.choices ? opt.choices.map((c: any) => ({
          label: c.label,
          extraCost: Number(c.extraCost) || 0,
        })) : [],
        min: opt.min !== null && opt.min !== undefined ? Number(opt.min) : null,
        max: opt.max !== null && opt.max !== undefined ? Number(opt.max) : null,
        step: opt.step !== null && opt.step !== undefined ? Number(opt.step) : null,
        unit: opt.unit || null,
      }));

      const { data } = await updateProductPricingMutation({
        variables: {
          productId: selectedProduct.productId,
          basePrice: Number(pricingForm.basePrice) || 0,
          options: cleanOptions,
        }
      });

      if (data?.updateProductPricing?.success) {
        setShowPricingModal(false);
        triggerToast('Product pricing updated successfully.', 'success');
      } else {
        setPricingError(data?.updateProductPricing?.message || 'Failed to save pricing.');
        triggerToast(data?.updateProductPricing?.message || 'Failed to save pricing.', 'danger');
      }
    } catch (err: any) {
      setPricingError(err.message || 'Pricing update failed.');
      triggerToast(err.message || 'Pricing update failed.', 'danger');
    }
  };

  // Job Cards Handlers
  const handleOpenCreateJobCard = (order: any) => {
    setIsEditingJobCard(false);
    setSelectedOrder(order);
    setSelectedJobCard(null);
    setJobCardForm({
      operator: '',
      designer: '',
      machine: '',
      material: '',
      finishingInstructions: '',
      priority: 'medium',
      notes: '',
    });
    setJobCardError('');
    setShowJobCardModal(true);
  };

  const handleOpenEditJobCard = (jobCard: any) => {
    setIsEditingJobCard(true);
    setSelectedOrder(null);
    setSelectedJobCard(jobCard);
    setJobCardForm({
      operator: jobCard.operator || '',
      designer: jobCard.designer || '',
      machine: jobCard.machine || '',
      material: jobCard.material || '',
      finishingInstructions: jobCard.finishingInstructions || '',
      priority: jobCard.priority || 'medium',
      notes: jobCard.notes || '',
    });
    setJobCardError('');
    setShowJobCardModal(true);
  };

  const handleSaveJobCard = async (e: React.FormEvent) => {
    e.preventDefault();

    const jobCardId = isEditingJobCard ? selectedJobCard.id : 'new';

    // First click - show confirmation
    if (confirmSaveJobCardId !== jobCardId) {
      setConfirmSaveJobCardId(jobCardId);
      return;
    }

    // Reset confirmation state
    setConfirmSaveJobCardId(null);
    setJobCardError('');

    try {
      if (isEditingJobCard) {
        const { data } = await updateJobCardMutation({
          variables: {
            id: selectedJobCard.id,
            operator: jobCardForm.operator,
            designer: jobCardForm.designer,
            machine: jobCardForm.machine,
            material: jobCardForm.material,
            finishingInstructions: jobCardForm.finishingInstructions,
            priority: jobCardForm.priority,
            notes: jobCardForm.notes,
          }
        });
        if (data?.updateJobCard?.success) {
          setShowJobCardModal(false);
          triggerToast('Job Card updated successfully!', 'success');
        } else {
          setJobCardError(data?.updateJobCard?.message || 'Failed to update Job Card.');
          triggerToast(data?.updateJobCard?.message || 'Failed to update Job Card.', 'danger');
        }
      } else {
        const { data } = await createJobCardMutation({
          variables: {
            orderId: selectedOrder.orderId,
            operator: jobCardForm.operator,
            designer: jobCardForm.designer,
            machine: jobCardForm.machine,
            material: jobCardForm.material,
            finishingInstructions: jobCardForm.finishingInstructions,
            priority: jobCardForm.priority,
            notes: jobCardForm.notes,
          }
        });
        if (data?.createJobCard?.success) {
          setShowJobCardModal(false);
          triggerToast('Job Card generated successfully!', 'success');
        } else {
          setJobCardError(data?.createJobCard?.message || 'Failed to create Job Card.');
          triggerToast(data?.createJobCard?.message || 'Failed to create Job Card.', 'danger');
        }
      }
    } catch (err: any) {
      setJobCardError(err.message || 'Operation failed.');
      triggerToast(err.message || 'Operation failed.', 'danger');
    }
  };

  const handleAdvanceJobCardStatus = async (jobCard: any) => {
    const statusOrder: Record<string, string> = {
      designing: 'pending_setup',
      pending_setup: 'printing',
      printing: 'finishing',
      finishing: 'ready',
      ready: 'completed',
    };
    const nextStatus = statusOrder[jobCard.status];
    if (!nextStatus) return;

    try {
      const { data } = await updateJobCardMutation({
        variables: {
          id: jobCard.id,
          status: nextStatus,
        }
      });
      if (data?.updateJobCard?.success) {
        triggerToast('Job status updated successfully!', 'success');
      } else {
        triggerToast(data?.updateJobCard?.message || 'Failed to update job status.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update status.', 'danger');
    }
  };

  const handleRevertJobCardStatus = async (jobCard: any) => {
    const reverseStatusOrder: Record<string, string> = {
      completed: 'ready',
      ready: 'finishing',
      finishing: 'printing',
      printing: 'pending_setup',
      pending_setup: 'designing',
    };
    const prevStatus = reverseStatusOrder[jobCard.status];
    if (!prevStatus) return;

    try {
      const { data } = await updateJobCardMutation({
        variables: {
          id: jobCard.id,
          status: prevStatus,
        }
      });
      if (data?.updateJobCard?.success) {
        triggerToast('Job status reverted successfully!', 'success');
      } else {
        triggerToast(data?.updateJobCard?.message || 'Failed to revert job status.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update status.', 'danger');
    }
  };

  const handleGenerateInvoice = async (jobCard: any) => {
    if (window.confirm(`Generate invoice for Job Card "${jobCard.jobCardId}"?`)) {
      try {
        const { data } = await generateInvoiceFromJobCard({
          variables: { jobCardId: jobCard.jobCardId },
        });
        if (data.generateInvoiceFromJobCard.success) {
          alert(`Success: ${data.generateInvoiceFromJobCard.message}`);
        } else {
          alert(`Failed: ${data.generateInvoiceFromJobCard.message}`);
        }
      } catch (err: any) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  const handleDeleteJobCard = async (jobCard: any) => {
    if (!window.confirm(`Are you sure you want to delete Job Card "${jobCard.jobCardId}"?`)) {
      return;
    }
    try {
      const { data } = await deleteJobCardMutation({
        variables: { id: jobCard.id }
      });
      if (data?.deleteJobCard?.success) {
        triggerToast('Job Card deleted successfully!', 'success');
      } else {
        triggerToast(data?.deleteJobCard?.message || 'Failed to delete Job Card.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to delete Job Card.', 'danger');
    }
  };

  const handleOpenTicketModal = (jobCard: any) => {
    setSelectedJobCard(jobCard);
    setShowTicketModal(true);
  };

  // Inventory handlers
  const handleOpenAddInventory = () => {
    setIsEditingInventory(false);
    setSelectedInventoryItem(null);
    setInventoryForm({
      name: '',
      category: 'Banner Printing',
      stock: 0,
      unit: 'pcs',
      minStock: 10,
      supplier: '',
      notes: '',
      orderDate: '',
      bannerType: '',
      bannerSize: '',
      overallLength: 0,
      rollLengthLimit: 10,
    });
    setInventoryFormError('');
    setShowInventoryModal(true);
  };

  const handleOpenEditInventory = (item: any) => {
    setIsEditingInventory(true);
    setSelectedInventoryItem(item);
    setInventoryForm({
      name: item.name,
      category: item.category,
      stock: item.stock,
      unit: item.unit,
      minStock: item.minStock,
      supplier: item.supplier || '',
      notes: item.notes || '',
      orderDate: item.orderDate || '',
      bannerType: item.bannerType || '',
      bannerSize: item.bannerSize || '',
      overallLength: item.overallLength || 0,
      rollLengthLimit: item.rollLengthLimit || 0,
    });
    setInventoryFormError('');
    setShowInventoryModal(true);
  };

  const handleSaveInventory = async (e: React.FormEvent) => {
    e.preventDefault();
    setInventoryFormError('');

    if (!inventoryForm.name.trim()) {
      setInventoryFormError('Item name is required.');
      return;
    }
    if (!inventoryForm.category) {
      setInventoryFormError('Category is required.');
      return;
    }
    if (inventoryForm.stock < 0) {
      setInventoryFormError('Stock cannot be negative.');
      return;
    }
    if (inventoryForm.minStock < 0) {
      setInventoryFormError('Minimum stock cannot be negative.');
      return;
    }

    try {
      const input = {
        name: inventoryForm.name.trim(),
        category: inventoryForm.category,
        stock: Number(inventoryForm.stock),
        unit: inventoryForm.unit.trim() || 'pcs',
        minStock: Number(inventoryForm.minStock),
        supplier: inventoryForm.supplier.trim(),
        notes: inventoryForm.notes.trim(),
        orderDate: inventoryForm.orderDate.trim(),
        bannerType: inventoryForm.category === 'Banner Printing' ? inventoryForm.bannerType : '',
        bannerSize: inventoryForm.category === 'Banner Printing' ? inventoryForm.bannerSize : '',
        overallLength: inventoryForm.category === 'Banner Printing' ? Number(inventoryForm.overallLength) : 0,
        rollLengthLimit: inventoryForm.category === 'Banner Printing' ? Number(inventoryForm.rollLengthLimit) : 0,
      };

      if (isEditingInventory && selectedInventoryItem) {
        const { data } = await updateInventoryItemMutation({
          variables: {
            id: selectedInventoryItem.id,
            input,
          }
        });
        if (data?.updateInventoryItem?.success) {
          setShowInventoryModal(false);
          triggerToast(data.updateInventoryItem.message || 'Inventory item updated successfully!', 'success');
        } else {
          setInventoryFormError(data?.updateInventoryItem?.message || 'Failed to update item.');
          triggerToast(data?.updateInventoryItem?.message || 'Failed to update item.', 'danger');
        }
      } else {
        const { data } = await createInventoryItemMutation({
          variables: {
            input,
          }
        });
        if (data?.createInventoryItem?.success) {
          setShowInventoryModal(false);
          triggerToast(data.createInventoryItem.message || 'Inventory item created successfully!', 'success');
        } else {
          setInventoryFormError(data?.createInventoryItem?.message || 'Failed to create item.');
          triggerToast(data?.createInventoryItem?.message || 'Failed to create item.', 'danger');
        }
      }
    } catch (err: any) {
      setInventoryFormError(err.message || 'Operation failed.');
      triggerToast(err.message || 'Operation failed.', 'danger');
    }
  };

  const handleDeleteInventory = async (item: any) => {
    if (!window.confirm(`Are you sure you want to delete inventory item "${item.name}"?`)) {
      return;
    }
    try {
      const { data } = await deleteInventoryItemMutation({
        variables: { id: item.id }
      });
      if (data?.deleteInventoryItem?.success) {
        triggerToast(data.deleteInventoryItem.message || 'Inventory item deleted successfully!', 'success');
      } else {
        triggerToast(data?.deleteInventoryItem?.message || 'Failed to delete item.', 'danger');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Delete operation failed.', 'danger');
    }
  };

  const handleExportExcel = () => {
    const dataToExport = filteredInventory.map((item: any) => ({
      'Item Name': item.name,
      'Category': item.category,
      'Banner Type': item.category === 'Banner Printing' ? (item.bannerType || '-') : '-',
      'Banner Size': item.category === 'Banner Printing' ? (item.bannerSize || '-') : '-',
      'Current Stock': item.category === 'Banner Printing' ? `${item.overallLength} m` : item.stock,
      'Unit': item.unit,
      'Min Stock Level': item.category === 'Banner Printing' ? `${item.rollLengthLimit} m` : item.minStock,
      'Stock Status': isOutOfStockItem(item) ? 'Out of Stock' : isLowStockItem(item) ? 'Low Stock' : 'In Stock',
      'Supplier': item.supplier || '-',
      'Notes': item.notes || '-',
      'Last Updated': new Date(Number(item.updatedAt) ? Number(item.updatedAt) : item.updatedAt).toLocaleDateString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory Stock');

    // Auto-fit column widths
    const maxLens = Object.keys(dataToExport[0] || {}).map(key => {
      let maxLen = key.length;
      dataToExport.forEach((row: any) => {
        const val = String(row[key] || '');
        if (val.length > maxLen) maxLen = val.length;
      });
      return { wch: maxLen + 3 };
    });
    worksheet['!cols'] = maxLens;

    XLSX.writeFile(workbook, `pdp_inventory_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const dateStr = new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    // Header banner
    doc.setFillColor(21, 101, 192); // dark primary blue
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('PRATHITHA DIGITAL PRINTERS', 15, 18);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Inventory Stock Status Report', 15, 26);
    doc.text(`Generated: ${dateStr}`, 15, 33);

    // Summary Metrics
    doc.setFillColor(248, 249, 250);
    doc.rect(15, 45, 180, 20, 'F');
    doc.setDrawColor(222, 226, 230);
    doc.rect(15, 45, 180, 20, 'S');

    const total = filteredInventory.length;
    const low = filteredInventory.filter(isLowStockItem).length;
    const out = filteredInventory.filter(isOutOfStockItem).length;

    doc.setTextColor(33, 37, 41);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Items: ${total}`, 25, 57);
    doc.text(`Low Stock: ${low}`, 85, 57);
    doc.text(`Out of Stock: ${out}`, 145, 57);

    // Table
    const tableColumn = ['Name', 'Category', 'Stock / Min', 'Status', 'Supplier', 'Last Updated'];
    const tableRows = filteredInventory.map((item: any) => {
      const statusText = isOutOfStockItem(item) ? 'Out of Stock' : isLowStockItem(item) ? 'Low Stock' : 'In Stock';
      const lastUpdated = new Date(Number(item.updatedAt) ? Number(item.updatedAt) : item.updatedAt).toLocaleDateString();
      const nameDetail = item.category === 'Banner Printing' && (item.bannerType || item.bannerSize)
        ? `${item.name}\n(Type: ${item.bannerType || '-'}, Size: ${item.bannerSize || '-'})`
        : item.name;
      const stockText = item.category === 'Banner Printing'
        ? `${item.overallLength} m / limit ${item.rollLengthLimit} m`
        : `${item.stock} / ${item.minStock} ${item.unit}`;
      return [
        nameDetail,
        item.category,
        stockText,
        statusText,
        item.supplier || '-',
        lastUpdated,
      ];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 72,
      theme: 'grid',
      headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 35 },
        2: { cellWidth: 25 },
        3: { cellWidth: 22 },
        4: { cellWidth: 30 },
        5: { cellWidth: 23 },
      },
      didParseCell: (data: any) => {
        if (data.section === 'body' && data.column.index === 3) {
          const text = data.cell.raw;
          if (text === 'Out of Stock') {
            data.cell.styles.textColor = [220, 53, 69];
            data.cell.styles.fontStyle = 'bold';
          } else if (text === 'Low Stock') {
            data.cell.styles.textColor = [253, 126, 20];
            data.cell.styles.fontStyle = 'bold';
          } else {
            data.cell.styles.textColor = [40, 167, 69];
          }
        }
      }
    });

    doc.save(`pdp_inventory_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleExportJobCardsExcel = () => {
    const filtered = jobCardsList.filter((jc: any) => {
      const searchLower = jobCardSearch.toLowerCase();
      const matchesSearch = jc.jobCardId.toLowerCase().includes(searchLower) ||
        (jc.customerId && jc.customerId.toLowerCase().includes(searchLower)) ||
        jc.customerName.toLowerCase().includes(searchLower) ||
        jc.operator.toLowerCase().includes(searchLower) ||
        (jc.designer && jc.designer.toLowerCase().includes(searchLower)) ||
        jc.machine.toLowerCase().includes(searchLower) ||
        jc.items.some((i: any) => i.productName.toLowerCase().includes(searchLower));
      const matchesStatus = jobCardStatusFilter === 'all' || jc.status === jobCardStatusFilter;
      return matchesSearch && matchesStatus;
    });

    const dataToExport = filtered.map((jc: any) => ({
      'Job Card ID': jc.jobCardId,
      'Order ID': jc.orderId,
      'Customer ID': jc.customerId || '-',
      'Customer Name': jc.customerName,
      'Customer Phone': jc.customerPhone,
      'Status': jc.status.replace('_', ' ').toUpperCase(),
      'Operator': jc.operator,
      'Designer': jc.designer || '-',
      'Machine': jc.machine,
      'Material': jc.material || '-',
      'Priority': jc.priority.toUpperCase(),
      'Date': new Date(Number(jc.createdAt) ? Number(jc.createdAt) : jc.createdAt).toLocaleDateString(),
    }));

    if (dataToExport.length === 0) {
      triggerToast('No job cards to export with current filters.', 'warning');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Job Cards');

    const maxLens = Object.keys(dataToExport[0] || {}).map(key => {
      let maxLen = key.length;
      dataToExport.forEach((row: any) => {
        const val = String(row[key] || '');
        if (val.length > maxLen) maxLen = val.length;
      });
      return { wch: maxLen + 3 };
    });
    worksheet['!cols'] = maxLens;

    XLSX.writeFile(workbook, `pdp_job_cards_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportJobCardsPDF = () => {
    const filtered = jobCardsList.filter((jc: any) => {
      const searchLower = jobCardSearch.toLowerCase();
      const matchesSearch = jc.jobCardId.toLowerCase().includes(searchLower) ||
        (jc.customerId && jc.customerId.toLowerCase().includes(searchLower)) ||
        jc.customerName.toLowerCase().includes(searchLower) ||
        jc.operator.toLowerCase().includes(searchLower) ||
        (jc.designer && jc.designer.toLowerCase().includes(searchLower)) ||
        jc.machine.toLowerCase().includes(searchLower) ||
        jc.items.some((i: any) => i.productName.toLowerCase().includes(searchLower));
      const matchesStatus = jobCardStatusFilter === 'all' || jc.status === jobCardStatusFilter;
      return matchesSearch && matchesStatus;
    });

    if (filtered.length === 0) {
      triggerToast('No job cards to export with current filters.', 'warning');
      return;
    }

    const doc = new jsPDF('landscape');
    const dateStr = new Date().toLocaleDateString('en-IN', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

    doc.setFillColor(21, 101, 192);
    doc.rect(0, 0, 297, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PRATHITHA DIGITAL PRINTERS', 15, 15);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Job Cards Report (Status: ${jobCardStatusFilter.toUpperCase()})`, 15, 23);
    doc.text(`Generated: ${dateStr}`, 190, 23);

    const tableColumn = ['Job ID', 'Customer', 'Status', 'Operator', 'Machine', 'Priority', 'Date'];
    const tableRows = filtered.map((jc: any) => [
      jc.jobCardId,
      `${jc.customerName}\n${jc.customerPhone}`,
      jc.status.replace('_', ' ').toUpperCase(),
      jc.operator,
      jc.machine,
      jc.priority.toUpperCase(),
      new Date(Number(jc.createdAt) ? Number(jc.createdAt) : jc.createdAt).toLocaleDateString()
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      theme: 'grid',
      headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 8.5, cellPadding: 3 },
    });

    doc.save(`pdp_job_cards_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Calculations for Metrics
  const ordersList = ordersData?.orders || [];
  const contactsList = contactsData?.contacts || [];
  const reviewsList = reviewsData?.reviews || [];
  const quotesList = quotesData?.quotes || [];
  const usersList = usersData?.users || [];
  const jobCardsList = jobCardsData?.jobCards || [];
  const inventoryList = inventoryData?.inventoryItems || [];

  const lowStockItemsCount = inventoryList.filter((item: any) => isOutOfStockItem(item) || isLowStockItem(item)).length;

  const filteredInventory = inventoryList.filter((item: any) => {
    const searchLower = inventorySearch.toLowerCase();
    const matchesSearch =
      item.name.toLowerCase().includes(searchLower) ||
      (item.supplier && item.supplier.toLowerCase().includes(searchLower)) ||
      (item.notes && item.notes.toLowerCase().includes(searchLower));

    const matchesCategory =
      inventoryCategoryFilter === 'all' || item.category === inventoryCategoryFilter;

    return matchesSearch && matchesCategory;
  });

  const filteredUsers = usersList.filter((u: any) =>
    u.role !== 'customer' && (u.username.toLowerCase().includes(userSearch.toLowerCase()) || (u.customerId && u.customerId.toLowerCase().includes(userSearch.toLowerCase())))
  );

  const customersList = React.useMemo(() => usersList.filter((u: any) => u.role === 'customer'), [usersList]);
  const filteredCustomers = React.useMemo(() => {
    const searchLower = customerSearch.toLowerCase();
    return customersList.filter((c: any) =>
      c.username.toLowerCase().includes(searchLower) ||
      (c.customerId && c.customerId.toLowerCase().includes(searchLower)) ||
      (c.companyName && c.companyName.toLowerCase().includes(searchLower)) ||
      (c.phone && c.phone.includes(searchLower)) ||
      (c.email && c.email.toLowerCase().includes(searchLower)) ||
      (c.gstNumber && c.gstNumber.toLowerCase().includes(searchLower))
    );
  }, [customersList, customerSearch]);

  const activeJobCardsCount = jobCardsList.filter((j: any) => j.status !== 'completed').length;

  const filteredJobCards = jobCardsList.filter((jc: any) => {
    const searchLower = jobCardSearch.toLowerCase();
    const matchesSearch = jc.jobCardId.toLowerCase().includes(searchLower) ||
      (jc.customerId && jc.customerId.toLowerCase().includes(searchLower)) ||
      jc.customerName.toLowerCase().includes(searchLower) ||
      jc.operator.toLowerCase().includes(searchLower) ||
      (jc.designer && jc.designer.toLowerCase().includes(searchLower)) ||
      jc.machine.toLowerCase().includes(searchLower) ||
      jc.items.some((i: any) => i.productName.toLowerCase().includes(searchLower));
    const matchesStatus = jobCardStatusFilter === 'all' || jc.status === jobCardStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalOrdersCount = ordersList.length;
  const pendingOrdersCount = ordersList.filter((o: any) => o.status === 'pending').length;
  const pendingQuotesCount = quotesList.filter((q: any) => q.status === 'pending').length;
  const unreadContactsCount = contactsList.filter((c: any) => !c.read).length;

  // Total Revenue of confirmed/completed/in_progress orders
  const activeStatuses = ['confirmed', 'in_progress', 'completed'];
  const totalRevenue = ordersList
    .filter((o: any) => activeStatuses.includes(o.status))
    .reduce((sum: number, o: any) => sum + (o.totalAmount || o.estimatedPrice || 0), 0);

  // Filtered Orders for the list
  const filteredOrders = ordersList.filter((order: any) => {
    const searchLower = orderSearch.toLowerCase();
    const matchesSearch =
      order.orderId.toLowerCase().includes(searchLower) ||
      (order.customerId && order.customerId.toLowerCase().includes(searchLower)) ||
      order.customerName.toLowerCase().includes(searchLower) ||
      (order.productName && order.productName.toLowerCase().includes(searchLower)) ||
      (order.items && order.items.some((item: any) => item.productName.toLowerCase().includes(searchLower)));

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleExportOrdersExcel = () => {
    if (filteredOrders.length === 0) {
      triggerToast('No orders to export with current filters.', 'warning');
      return;
    }

    const dataToExport = filteredOrders.map((order: any) => ({
      'Order ID': order.orderId,
      'Customer ID': order.customerId || '-',
      'Customer Name': order.customerName,
      'Phone': order.phone,
      'Email': order.email || '-',
      'Total Amount': order.totalAmount || order.estimatedPrice || 0,
      'Advance Amount': order.advanceAmount || 0,
      'Balance Due': (order.totalAmount || order.estimatedPrice || 0) - (order.advanceAmount || 0),
      'Status': order.status.replace('_', ' ').toUpperCase(),
      'Payment Status': order.paymentStatus ? order.paymentStatus.toUpperCase() : '-',
      'Products': (order.items || []).map((i: any) => `${i.productName} (Qty: ${i.quantity})`).join(', ') || order.productName,
      'Date': new Date(Number(order.createdAt) ? Number(order.createdAt) : order.createdAt).toLocaleDateString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

    const maxLens = Object.keys(dataToExport[0] || {}).map(key => {
      let maxLen = key.length;
      dataToExport.forEach((row: any) => {
        const val = String(row[key] || '');
        if (val.length > maxLen) maxLen = val.length;
      });
      return { wch: maxLen + 3 };
    });
    worksheet['!cols'] = maxLens;

    XLSX.writeFile(workbook, `pdp_orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportOrdersPDF = () => {
    if (filteredOrders.length === 0) {
      triggerToast('No orders to export with current filters.', 'warning');
      return;
    }

    const doc = new jsPDF('landscape');
    const dateStr = new Date().toLocaleDateString('en-IN', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

    doc.setFillColor(21, 101, 192);
    doc.rect(0, 0, 297, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PRATHITHA DIGITAL PRINTERS', 15, 15);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Orders Report (Status: ${statusFilter.toUpperCase()})`, 15, 23);
    doc.text(`Generated: ${dateStr}`, 190, 23);

    const tableColumn = ['Order ID', 'Customer', 'Products', 'Amount (Rs)', 'Advance (Rs)', 'Balance (Rs)', 'Status', 'Date'];
    const tableRows = filteredOrders.map((order: any) => [
      order.orderId,
      `${order.customerName}\n${order.phone}`,
      (order.items || []).map((i: any) => `${i.productName} (x${i.quantity})`).join('\n') || order.productName,
      order.totalAmount || order.estimatedPrice || 0,
      order.advanceAmount || 0,
      (order.totalAmount || order.estimatedPrice || 0) - (order.advanceAmount || 0),
      order.status.replace('_', ' ').toUpperCase(),
      new Date(Number(order.createdAt) ? Number(order.createdAt) : order.createdAt).toLocaleDateString()
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      theme: 'grid',
      headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 8.5, cellPadding: 3 },
    });

    doc.save(`pdp_orders_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning" text="dark" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">Pending</Badge>;
      case 'confirmed':
        return <Badge bg="primary" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">Confirmed</Badge>;
      case 'in_progress':
        return <Badge bg="info" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">In Progress</Badge>;
      case 'completed':
        return <Badge bg="success" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">Completed</Badge>;
      case 'cancelled':
        return <Badge bg="danger" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">Cancelled</Badge>;
      default:
        return <Badge bg="secondary" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">{status}</Badge>;
    }
  };

  return (
    <div className="page-fade d-flex flex-column admin-page-wrapper" style={{ minHeight: 'calc(100vh - 68px)', backgroundColor: 'var(--bg-secondary)' }}>
      <div className="flex-grow-1 py-4">
        <Container fluid style={{ maxWidth: '1600px', padding: '0 20px' }}>
          <div className="d-flex gap-3 gap-md-4 align-items-start position-relative">
            {/* Collapsible Side Navigation */}
            <AdminTabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              unreadContactsCount={unreadContactsCount}
              pendingQuotesCount={pendingQuotesCount}
              activeJobCardsCount={activeJobCardsCount}
              lowStockItemsCount={lowStockItemsCount}
            />

            {/* Main Content Area */}
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <AdminDashboardHeader
                totalOrdersCount={totalOrdersCount}
                pendingQuotesCount={pendingQuotesCount}
                unreadContactsCount={unreadContactsCount}
                totalRevenue={totalRevenue}
                onLogout={handleLogout}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
              />

              {/* Tab Content */}
              <Card className="border-0 shadow-sm rounded-4 bg-white p-4" style={{ minHeight: '500px' }}>

                {/* SALES TAB */}
                {/* {activeTab === 'sales' && (
                  <div className="step-content-fade">
                    <SalesTracker />
                  </div>
                )} */}

                {/* PURCHASE TAB */}
                {activeTab === 'purchase' && (
                  <div className="step-content-fade">
                    <PurchaseTracker />
                  </div>
                )}

                {/* ORDERS TAB */}

                {activeTab === 'orders' && (
                  <div className="step-content-fade">
                    <div className="d-flex align-items-center mb-4 gap-2 flex-wrap">
                      <h4 className="fw-bold text-dark mb-0 me-auto">Customer Orders</h4>
                      <Form.Control
                        type="text"
                        placeholder="Search ID, customer, product..."
                        value={orderSearch}
                        onChange={(e) => { setOrderSearch(e.target.value); setOrdersPage(1); }}
                        style={{ width: '240px', borderRadius: '8px', fontSize: '13px' }}
                      />
                      <Form.Select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setOrdersPage(1); }}
                        style={{ width: '150px', borderRadius: '8px', fontSize: '13px' }}
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </Form.Select>
                      <div className="btn-group border rounded-3 p-0.5 bg-light me-2" style={{ height: '36px' }}>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={handleExportOrdersExcel}
                          className="px-3 py-1 border-0 fw-semibold text-muted bg-transparent"
                          style={{ fontSize: '12.5px' }}
                          title="Export filtered orders to Excel"
                        >
                          <i className="fas fa-file-excel text-success me-1"></i> Excel
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={handleExportOrdersPDF}
                          className="px-3 py-1 border-0 fw-semibold text-muted bg-transparent"
                          style={{ fontSize: '12.5px' }}
                          title="Export filtered orders to PDF"
                        >
                          <i className="fas fa-file-pdf text-danger me-1"></i> PDF
                        </Button>
                      </div>
                      <Button
                        variant="primary"
                        className="fw-semibold d-flex align-items-center gap-2"
                        style={{ borderRadius: '8px', fontSize: '13px', whiteSpace: 'nowrap', height: '36px' }}
                        onClick={() => setShowCreateOrderModal(true)}
                      >
                        <i className="fas fa-plus" />
                        Create Order
                      </Button>
                    </div>

                    {ordersLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading orders...</p>
                      </div>
                    ) : filteredOrders.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-info-circle mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No orders found.</div>
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                          <thead className="table-light">
                            <tr style={{ fontSize: '12px', color: '#64748b' }}>
                              <th className="border-0 py-3">Order ID</th>
                              <th className="border-0 py-3">Customer</th>
                              <th className="border-0 py-3">Product</th>
                              <th className="border-0 py-3">Qty</th>
                              <th className="border-0 py-3">Price</th>
                              <th className="border-0 py-3">Advance</th>
                              <th className="border-0 py-3">Date</th>
                              <th className="border-0 py-3 text-center">Status</th>
                              <th className="border-0 py-3 text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredOrders.slice((ordersPage - 1) * ITEMS_PER_PAGE, ordersPage * ITEMS_PER_PAGE).map((order: any) => (
                              <tr key={order.id} className="bg-light-row" style={{ fontSize: '13px' }}>
                                <td className="fw-semibold text-dark font-monospace py-3">{order.orderId}</td>
                                <td>
                                  <div className="fw-bold text-dark">{order.customerName}</div>
                                  {order.customerId && <div className="text-muted font-monospace mt-1" style={{ fontSize: '10.5px' }}><i className="fas fa-id-card me-1"></i>{order.customerId}</div>}
                                  <div className="text-muted" style={{ fontSize: '11px', marginTop: order.customerId ? '2px' : '0' }}>{order.customerPhone}</div>
                                </td>
                                <td>
                                  {order.items && order.items.length > 0
                                    ? `${order.items[0].productName}${order.items.length > 1 ? ` + ${order.items.length - 1} more` : ''}`
                                    : (order.productName || 'No products')}
                                </td>
                                <td>
                                  {order.items && order.items.length > 0
                                    ? order.items.reduce((acc: number, item: any) => acc + item.quantity, 0).toLocaleString()
                                    : (order.quantity || 0)}
                                </td>
                                <td className="fw-bold text-dark">
                                  ₹{(order.totalAmount || order.estimatedPrice || 0).toLocaleString('en-IN')}
                                </td>
                                <td>
                                  <div className="fw-semibold text-success">
                                    ₹{(order.advanceAmount || 0).toLocaleString('en-IN')}
                                  </div>
                                  {((order.totalAmount || order.estimatedPrice || 0) > (order.advanceAmount || 0)) && (
                                    <div className="text-muted" style={{ fontSize: '10.5px' }}>
                                      Bal: ₹{((order.totalAmount || order.estimatedPrice || 0) - (order.advanceAmount || 0)).toLocaleString('en-IN')}
                                    </div>
                                  )}
                                </td>
                                <td>{new Date(Number(order.createdAt) ? Number(order.createdAt) : order.createdAt).toLocaleDateString()}</td>
                                <td className="text-center">
                                  <div>{getStatusBadge(order.status)}</div>
                                  <div className="mt-1">
                                    {order.paymentStatus === 'paid' ? (
                                      <Badge bg="success" className="px-2 py-0.5 rounded-pill" style={{ fontSize: '10px' }}>Paid</Badge>
                                    ) : (
                                      <Badge bg="warning" text="dark" className="px-2 py-0.5 rounded-pill" style={{ fontSize: '10px' }}>Unpaid</Badge>
                                    )}
                                  </div>
                                </td>
                                <td className="text-end">
                                  <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="fw-semibold px-2.5"
                                      style={{ borderRadius: '6px', fontSize: '12px' }}
                                      onClick={() => {
                                        setSelectedOrder(order);
                                        setIsEditOrderMode(false);
                                        setShowOrderModal(true);
                                      }}
                                    >
                                      Details
                                    </Button>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      className="fw-semibold px-2.5 d-flex align-items-center gap-1"
                                      style={{ borderRadius: '6px', fontSize: '12px' }}
                                      onClick={() => {
                                        setSelectedOrder(order);
                                        setIsEditOrderMode(true);
                                        setShowOrderModal(true);
                                      }}
                                      title="Edit order details"
                                    >
                                      <i className="fas fa-edit"></i> Edit
                                    </Button>
                                    {order.hasJobCard ? (
                                      (() => {
                                        const job = jobCardsList.find((jc: any) => jc.orderId === order.orderId);
                                        return (
                                          <Button
                                            variant="outline-success"
                                            size="sm"
                                            className="fw-semibold px-2.5 d-flex align-items-center gap-1"
                                            style={{ borderRadius: '6px', fontSize: '12px' }}
                                            onClick={() => job && handleOpenTicketModal(job)}
                                            disabled={!job}
                                          >
                                            <i className="fas fa-print"></i> Ticket
                                          </Button>
                                        );
                                      })()
                                    ) : (
                                      <Button
                                        variant={order.paymentStatus === 'paid' || order.status === 'confirmed' ? 'success' : 'outline-secondary'}
                                        size="sm"
                                        className="fw-semibold px-2.5 d-flex align-items-center gap-1"
                                        style={{ borderRadius: '6px', fontSize: '12px', color: (order.paymentStatus === 'paid' || order.status === 'confirmed') ? '#fff' : undefined }}
                                        onClick={() => handleOpenCreateJobCard(order)}
                                      >
                                        <i className="fas fa-plus-circle"></i> Create Job
                                      </Button>
                                    )}
                                    <Button
                                      variant={confirmDeleteId === order.orderId ? 'danger' : 'outline-danger'}
                                      size="sm"
                                      className="fw-semibold"
                                      style={{ borderRadius: '6px', fontSize: '12px' }}
                                      onClick={() => handleDeleteOrder(order.orderId)}
                                      title="Delete order"
                                    >
                                      {confirmDeleteId === order.orderId
                                        ? <><i className="fas fa-exclamation-triangle me-1" />Confirm?</>
                                        : <i className="fas fa-trash" />}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <AdminPagination currentPage={ordersPage} totalItems={filteredOrders.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setOrdersPage} />
                      </div>
                    )}

                    {/* Create Order Modal */}
                    <CreateOrderModal
                      show={showCreateOrderModal}
                      onHide={() => setShowCreateOrderModal(false)}
                      onSuccess={(msg) => {
                        triggerToast(msg, 'success');
                        refetchOrders();
                        refetchJobCards();
                      }}
                    />
                  </div>
                )}

                {/* CONTACTS TAB */}
                {activeTab === 'contacts' && (
                  <div className="step-content-fade">
                    <div className="d-flex align-items-center mb-4 gap-2 flex-wrap">
                      <h4 className="fw-bold text-dark mb-0 me-auto">Customer Enquiries</h4>
                      <Button
                        variant="primary"
                        className="fw-semibold d-flex align-items-center gap-2"
                        style={{ borderRadius: '8px', fontSize: '13px', whiteSpace: 'nowrap' }}
                        onClick={() => setShowCreateEnquiryModal(true)}
                      >
                        <i className="fas fa-plus" />
                        Create Enquiry
                      </Button>
                    </div>

                    {contactsLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading enquiries...</p>
                      </div>
                    ) : contactsList.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-info-circle mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No enquiries submitted yet.</div>
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                          <thead className="table-light">
                            <tr style={{ fontSize: '12px', color: '#64748b' }}>
                              <th className="py-3">Sender</th>
                              <th className="py-3">Source</th>
                              <th className="py-3">Message</th>
                              <th className="py-3">Date</th>
                              <th className="py-3 text-center">Status</th>
                              <th className="py-3 text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {contactsList.slice((contactsPage - 1) * ITEMS_PER_PAGE, contactsPage * ITEMS_PER_PAGE).map((contact: any) => (
                              <tr key={contact.id} className={!contact.read ? 'fw-bold bg-light-row' : ''} style={{ fontSize: '13px' }}>
                                <td className="py-3">
                                  <div className="text-dark">{contact.name}</div>
                                  <div className="text-muted fw-normal" style={{ fontSize: '11px' }}>{contact.email}</div>
                                  <div className="text-muted fw-normal" style={{ fontSize: '11px' }}>{contact.phone}</div>
                                </td>
                                <td>
                                  <Badge
                                    bg={contact.type === 'walk-in' ? 'info' : 'primary'}
                                    className="px-2.5 py-1 rounded-pill text-capitalize"
                                    style={{ fontSize: '10.5px' }}
                                  >
                                    <i className={contact.type === 'walk-in' ? 'fas fa-walking me-1' : 'fas fa-globe me-1'} />
                                    {contact.type === 'walk-in' ? 'walk-in' : 'website'}
                                  </Badge>
                                </td>
                                <td style={{ maxWidth: '350px' }}>
                                  <p className="mb-0 text-truncate-2 fw-normal" style={{ fontSize: '12.5px', whiteSpace: 'pre-line' }}>{contact.message}</p>
                                </td>
                                <td>{new Date(Number(contact.createdAt) ? Number(contact.createdAt) : contact.createdAt).toLocaleDateString()}</td>
                                <td className="text-center">
                                  {contact.read ? (
                                    <Badge bg="secondary" className="px-2 py-1 rounded-pill">Read</Badge>
                                  ) : (
                                    <Badge bg="danger" className="px-2 py-1 rounded-pill">Unread</Badge>
                                  )}
                                </td>
                                <td className="text-end">
                                  <div className="d-flex align-items-center justify-content-end gap-2">
                                    {!contact.read && (
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        className="fw-semibold"
                                        style={{ borderRadius: '6px' }}
                                        onClick={() => handleMarkRead(contact.id)}
                                      >
                                        Mark as Read
                                      </Button>
                                    )}
                                    <Button
                                      variant={confirmDeleteId === contact.id ? 'danger' : 'outline-danger'}
                                      size="sm"
                                      className="fw-semibold"
                                      style={{ borderRadius: '6px', fontSize: '12px' }}
                                      onClick={() => handleDeleteContact(contact.id)}
                                      title="Delete enquiry"
                                    >
                                      {confirmDeleteId === contact.id
                                        ? <><i className="fas fa-exclamation-triangle me-1" />Confirm?</>
                                        : <i className="fas fa-trash" />}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <AdminPagination currentPage={contactsPage} totalItems={contactsList.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setContactsPage} />
                      </div>
                    )}
                    {/* Create Enquiry Modal */}
                    <CreateEnquiryModal
                      show={showCreateEnquiryModal}
                      onHide={() => setShowCreateEnquiryModal(false)}
                      onSuccess={(msg) => {
                        triggerToast(msg, 'success');
                        refetchContacts();
                      }}
                    />
                  </div>
                )}

                {/* REVIEWS TAB */}
                {activeTab === 'reviews' && (
                  <div className="step-content-fade">
                    <h4 className="fw-bold text-dark mb-4">Customer Reviews</h4>

                    {reviewsLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading reviews...</p>
                      </div>
                    ) : reviewsList.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-info-circle mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No reviews found.</div>
                      </Alert>
                    ) : (
                      <>
                        <Row className="g-3">
                          {reviewsList.slice((reviewsPage - 1) * ITEMS_PER_PAGE, reviewsPage * ITEMS_PER_PAGE).map((review: any) => (
                            <Col key={review.id} xs={12} md={6}>
                              <Card className="border border-light h-100 rounded-4 bg-white p-3 shadow-sm">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <div className="d-flex align-items-center gap-3">
                                    <div
                                      className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold"
                                      style={{ width: '40px', height: '40px', backgroundColor: review.avatarColor || '#333', fontSize: '16px' }}
                                    >
                                      {review.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <h6 className="fw-bold text-dark mb-0">{review.name}</h6>
                                      <span className="text-muted" style={{ fontSize: '11px' }}>{review.email}</span>
                                    </div>
                                  </div>
                                  <div>
                                    {review.approved ? (
                                      <Badge bg="success" className="rounded-pill px-2 py-1">Approved</Badge>
                                    ) : (
                                      <Badge bg="warning" text="dark" className="rounded-pill px-2 py-1">Pending Approval</Badge>
                                    )}
                                  </div>
                                </div>

                                {/* Rating Stars */}
                                <div className="text-warning mb-2" style={{ fontSize: '12px' }}>
                                  {Array.from({ length: 5 }).map((_, idx) => (
                                    <i key={idx} className={`${idx < review.rating ? 'fas' : 'far'} fa-star me-1`}></i>
                                  ))}
                                </div>

                                <p className="text-muted mb-3 flex-grow-1" style={{ fontSize: '13px', lineHeight: '1.5' }}>
                                  "{review.comment}"
                                </p>

                                <div className="d-flex justify-content-between align-items-center border-top pt-2">
                                  <span className="text-muted" style={{ fontSize: '11px' }}>{new Date(Number(review.createdAt) ? Number(review.createdAt) : review.createdAt).toLocaleDateString()}</span>
                                  <div className="d-flex gap-2">
                                    {!review.approved && (
                                      <Button
                                        variant="success"
                                        size="sm"
                                        className="fw-semibold px-3"
                                        style={{ borderRadius: '6px' }}
                                        onClick={() => handleApproveReview(review.id)}
                                      >
                                        <i className="fas fa-check me-1"></i> Approve Review
                                      </Button>
                                    )}
                                    <Button
                                      variant={confirmDeleteId === review.id ? 'danger' : 'outline-danger'}
                                      size="sm"
                                      className="fw-semibold"
                                      style={{ borderRadius: '6px', fontSize: '12px' }}
                                      onClick={() => handleDeleteReview(review.id)}
                                      title="Delete review"
                                    >
                                      {confirmDeleteId === review.id
                                        ? <><i className="fas fa-exclamation-triangle me-1" />Confirm?</>
                                        : <i className="fas fa-trash" />}
                                    </Button>
                                  </div>
                                </div>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                        <AdminPagination currentPage={reviewsPage} totalItems={reviewsList.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setReviewsPage} />
                      </>
                    )}
                  </div>
                )}

                {/* QUOTES TAB */}
                {activeTab === 'quotes' && (
                  <div className="step-content-fade">
                    <div className="d-flex align-items-center mb-4 gap-2 flex-wrap">
                      <h4 className="fw-bold text-dark mb-0 me-auto">Print Quote Requests</h4>
                      <Button
                        variant="primary"
                        className="fw-semibold d-flex align-items-center gap-2"
                        style={{ borderRadius: '8px', fontSize: '13px', whiteSpace: 'nowrap' }}
                        onClick={() => setShowCreateQuoteModal(true)}
                      >
                        <i className="fas fa-plus" />
                        Create Quote
                      </Button>
                    </div>

                    {quotesLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading quotes...</p>
                      </div>
                    ) : quotesList.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-info-circle mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No quote requests submitted yet.</div>
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                          <thead className="table-light">
                            <tr style={{ fontSize: '12px', color: '#64748b' }}>
                              <th className="py-3">Quote ID</th>
                              <th className="py-3">Customer</th>
                              <th className="py-3">Source</th>
                              <th className="py-3">Product / Qty</th>
                              <th className="py-3">Specs / Requirements</th>
                              <th className="py-3 text-center">Status</th>
                              <th className="py-3 text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotesList.slice((quotesPage - 1) * ITEMS_PER_PAGE, quotesPage * ITEMS_PER_PAGE).map((quote: any) => (
                              <tr key={quote.id} style={{ fontSize: '13px' }}>
                                <td className="fw-semibold text-dark font-monospace py-3">{quote.quoteId}</td>
                                <td>
                                  <div className="fw-bold text-dark">{quote.name}</div>
                                  <div className="text-muted" style={{ fontSize: '11px' }}>{quote.phone}</div>
                                  <div className="text-muted" style={{ fontSize: '11px' }}>{quote.email}</div>
                                </td>
                                <td>
                                  <Badge
                                    bg={quote.type === 'walk-in' ? 'info' : 'primary'}
                                    className="px-2.5 py-1 rounded-pill text-capitalize"
                                    style={{ fontSize: '10.5px' }}
                                  >
                                    <i className={quote.type === 'walk-in' ? 'fas fa-walking me-1' : 'fas fa-globe me-1'} />
                                    {quote.type === 'walk-in' ? 'walk-in' : 'website'}
                                  </Badge>
                                </td>
                                <td>
                                  {quote.items && quote.items.length > 0 ? (
                                    <div className="d-flex flex-column gap-2">
                                      {quote.items.map((item: any, idx: number) => (
                                        <div key={idx} className={idx > 0 ? "border-top pt-2 mt-1" : ""}>
                                          <div className="fw-bold text-dark text-capitalize">{item.product.replace('-', ' ')}</div>
                                          <div className="text-muted" style={{ fontSize: '11px' }}>Qty: {item.quantity} {item.size && ` | Size: ${item.size}`}</div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <>
                                      <div className="fw-bold text-dark text-capitalize">{quote.product?.replace('-', ' ') || 'Unknown'}</div>
                                      <div className="text-muted" style={{ fontSize: '11px' }}>Qty: {quote.quantity}</div>
                                      {quote.size && <div className="text-muted" style={{ fontSize: '11px' }}>Size: {quote.size}</div>}
                                    </>
                                  )}
                                </td>
                                <td style={{ maxWidth: '280px' }}>
                                  {quote.items && quote.items.length > 0 ? (
                                    <div className="d-flex flex-column gap-2">
                                      {quote.items.map((item: any, idx: number) => (
                                        <div key={idx} className={idx > 0 ? "border-top pt-2 mt-1" : ""}>
                                          <p className="mb-0 text-truncate-2 text-muted" style={{ fontSize: '12px', whiteSpace: 'pre-line' }}>
                                            {item.description || 'No custom description'}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="mb-0 text-truncate-2 text-muted" style={{ fontSize: '12px', whiteSpace: 'pre-line' }}>
                                      {quote.description || 'No custom description'}
                                    </p>
                                  )}
                                </td>
                                <td className="text-center">
                                  {quote.status === 'pending' && <Badge bg="warning" text="dark" className="rounded-pill px-2 py-1">Pending</Badge>}
                                  {quote.status === 'contacted' && <Badge bg="info" className="rounded-pill px-2 py-1">Contacted</Badge>}
                                  {quote.status === 'processed' && <Badge bg="success" className="rounded-pill px-2 py-1">Processed</Badge>}
                                  {quote.status === 'cancelled' && <Badge bg="danger" className="rounded-pill px-2 py-1">Cancelled</Badge>}
                                </td>
                                <td className="text-end">
                                  <div className="d-flex align-items-center justify-content-end gap-2">
                                    {updatingQuoteId === quote.quoteId && <Spinner size="sm" animation="border" />}
                                    <Form.Select
                                      size="sm"
                                      value={quote.status}
                                      onChange={(e) => handleUpdateQuoteStatus(quote.quoteId, e.target.value)}
                                      style={{ width: '120px', borderRadius: '6px', fontSize: '12px' }}
                                      disabled={updatingQuoteId === quote.quoteId}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="contacted">Contacted</option>
                                      <option value="processed">Processed</option>
                                      <option value="cancelled">Cancelled</option>
                                    </Form.Select>
                                    <Button
                                      variant={confirmDeleteId === quote.quoteId ? 'danger' : 'outline-danger'}
                                      size="sm"
                                      className="fw-semibold"
                                      style={{ borderRadius: '6px', fontSize: '12px' }}
                                      onClick={() => handleDeleteQuote(quote.quoteId)}
                                      title="Delete quote"
                                    >
                                      {confirmDeleteId === quote.quoteId
                                        ? <><i className="fas fa-exclamation-triangle me-1" />Confirm?</>
                                        : <i className="fas fa-trash" />}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <AdminPagination currentPage={quotesPage} totalItems={quotesList.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setQuotesPage} />
                      </div>
                    )}
                    {/* Create Quote Modal */}
                    <CreateQuoteModal
                      show={showCreateQuoteModal}
                      onHide={() => setShowCreateQuoteModal(false)}
                      onSuccess={(msg) => {
                        triggerToast(msg, 'success');
                        refetchQuotes();
                      }}
                    />
                  </div>
                )}

                {/* USERS TAB */}
                {activeTab === 'users' && (
                  <div className="step-content-fade">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                      <h4 className="fw-bold text-dark mb-0">User Accounts Management</h4>
                      <div className="d-flex gap-2 flex-wrap">
                        <Form.Control
                          type="text"
                          placeholder="Search username..."
                          value={userSearch}
                          onChange={(e) => { setUserSearch(e.target.value); setUsersPage(1); }}
                          style={{ maxWidth: '240px', borderRadius: '8px', fontSize: '13px' }}
                        />
                        <Button
                          variant="primary"
                          onClick={handleOpenAddUser}
                          className="fw-semibold d-flex align-items-center gap-2 px-3"
                          style={{ borderRadius: '8px', fontSize: '13.5px' }}
                        >
                          <i className="fas fa-user-plus"></i> Add New User
                        </Button>
                      </div>
                    </div>

                    {usersLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading users...</p>
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-info-circle mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No user accounts found.</div>
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                          <thead className="table-light">
                            <tr style={{ fontSize: '12px', color: '#64748b' }}>
                              <th className="border-0 py-3">Username</th>
                              <th className="border-0 py-3">Account ID</th>
                              <th className="border-0 py-3 text-center">Role</th>
                              <th className="border-0 py-3">Date Created</th>
                              <th className="border-0 py-3 text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredUsers.slice((usersPage - 1) * ITEMS_PER_PAGE, usersPage * ITEMS_PER_PAGE).map((user: any) => {
                              const isSelf = user.username.toLowerCase() === localStorage.getItem('username')?.toLowerCase();
                              return (
                                <tr key={user.id} className="bg-light-row" style={{ fontSize: '13.5px' }}>
                                  <td className="fw-semibold text-dark py-3">
                                    <div className="d-flex align-items-center gap-2">
                                      <div
                                        className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          backgroundColor:
                                            (user.role === 'admin' || user.role === 'admin-manager') ? '#3b5bdb' :
                                              user.role === 'customer' ? 'var(--pink)' :
                                                '#0ca678',
                                          fontSize: '12px'
                                        }}
                                      >
                                        {user.username.charAt(0).toUpperCase()}
                                      </div>
                                      <div>
                                        <span>{user.username}</span> {isSelf && <Badge bg="success" className="ms-1 px-2 py-0.5 rounded-pill" style={{ fontSize: '10px' }}>You</Badge>}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    {user.customerId ? (
                                      <div className="text-muted font-monospace" style={{ fontSize: '12.5px' }}><i className="fas fa-id-card me-1 text-secondary"></i>{user.customerId}</div>
                                    ) : (
                                      <span className="text-muted" style={{ fontSize: '12px', fontStyle: 'italic' }}>-</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {(user.role === 'admin' || user.role === 'admin-manager') ? (
                                      <Badge bg="primary-subtle" className="text-primary px-3 py-1.5 rounded-pill text-capitalize" style={{ fontSize: '11px', fontWeight: 600 }}>{user.role.replace('-', ' ')}</Badge>
                                    ) : user.role === 'customer' ? (
                                      <Badge bg="success-subtle" className="text-success px-3 py-1.5 rounded-pill text-capitalize" style={{ fontSize: '11px', fontWeight: 600 }}>Customer</Badge>
                                    ) : (
                                      <Badge bg="warning-subtle" className="text-warning-emphasis px-3 py-1.5 rounded-pill text-capitalize" style={{ fontSize: '11px', fontWeight: 600 }}>
                                        {user.role.replace(/[_-]/g, ' ')}
                                      </Badge>
                                    )}
                                  </td>
                                  <td>{new Date(Number(user.createdAt) ? Number(user.createdAt) : user.createdAt).toLocaleDateString()}</td>
                                  <td className="text-end">
                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="fw-semibold px-2 py-1"
                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                        onClick={() => handleOpenEditUser(user)}
                                      >
                                        <i className="fas fa-edit"></i> Edit
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="fw-semibold px-2 py-1"
                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                        onClick={() => handleDeleteUser(user)}
                                        disabled={isSelf}
                                      >
                                        <i className="fas fa-trash-alt"></i> Delete
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        <AdminPagination currentPage={usersPage} totalItems={filteredUsers.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setUsersPage} />
                      </div>
                    )}
                  </div>
                )}

                {/* CUSTOMERS TAB */}
                {activeTab === 'customers' && (
                  <div className="step-content-fade">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                      <div>
                        <h4 className="fw-bold text-dark mb-1">Customer Management</h4>
                        <p className="text-muted mb-0" style={{ fontSize: '12.5px' }}>View, search, edit and delete customer profiles.</p>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Form.Control
                          type="text"
                          placeholder="Search name, company, phone, email, GST..."
                          value={customerSearch}
                          onChange={(e) => { setCustomerSearch(e.target.value); setCustomersPage(1); }}
                          style={{ minWidth: '240px', borderRadius: '8px', fontSize: '13px' }}
                        />
                        <Button
                          variant="primary"
                          onClick={handleOpenAddCustomer}
                          className="fw-semibold d-flex align-items-center gap-2 px-3"
                          style={{ borderRadius: '8px', fontSize: '13.5px' }}
                        >
                          <i className="fas fa-user-plus"></i> Add New Customer
                        </Button>
                      </div>
                    </div>

                    {usersLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading customers...</p>
                      </div>
                    ) : filteredCustomers.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-info-circle mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No customer accounts found.</div>
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                          <thead className="table-light">
                            <tr style={{ fontSize: '12px', color: '#64748b' }}>
                              <th className="border-0 py-3">Customer Name</th>
                              <th className="border-0 py-3">Customer ID</th>
                              <th className="border-0 py-3">Company Details</th>
                              <th className="border-0 py-3">Contact Info</th>
                              <th className="border-0 py-3">Billing & Shipping Addresses</th>
                              <th className="border-0 py-3 text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCustomers.slice((customersPage - 1) * ITEMS_PER_PAGE, customersPage * ITEMS_PER_PAGE).map((customer: any) => {
                              return (
                                <tr key={customer.id} className="bg-light-row" style={{ fontSize: '13.5px' }}>
                                  <td className="fw-semibold text-dark py-3" style={{ verticalAlign: 'top' }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <div
                                        className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          backgroundColor: '#e64980',
                                          fontSize: '12px'
                                        }}
                                      >
                                        {customer.username.charAt(0).toUpperCase()}
                                      </div>
                                      <div>
                                        <div style={{ lineHeight: '1.2' }}>{customer.username}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{ verticalAlign: 'top' }}>
                                    {customer.customerId ? (
                                      <div className="text-muted font-monospace" style={{ fontSize: '12.5px', marginTop: '2px' }}><i className="fas fa-id-card me-1 text-secondary"></i>{customer.customerId}</div>
                                    ) : (
                                      <span className="text-muted" style={{ fontSize: '12px', fontStyle: 'italic', marginTop: '2px' }}>-</span>
                                    )}
                                  </td>
                                  <td style={{ verticalAlign: 'top' }}>
                                    <div className="fw-bold">{customer.companyName || '-'}</div>
                                    {customer.gstNumber && (
                                      <div className="text-muted" style={{ fontSize: '11.5px' }}>
                                        <Badge bg="secondary-subtle" className="text-secondary border px-2 py-0.5 mt-1">GST: {customer.gstNumber}</Badge>
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ verticalAlign: 'top' }}>
                                    <div><i className="fas fa-phone text-muted me-1.5" style={{ fontSize: '12px' }}></i>{customer.phone || '-'}</div>
                                    <div className="text-muted" style={{ fontSize: '12px', marginTop: '2px' }}>
                                      <i className="fas fa-envelope text-muted me-1.5" style={{ fontSize: '12px' }}></i>{customer.email || '-'}
                                    </div>
                                  </td>
                                  <td style={{ verticalAlign: 'top', maxWidth: '300px' }}>
                                    <div style={{ fontSize: '12.5px' }}>
                                      <strong className="text-muted" style={{ fontSize: '10px', textTransform: 'uppercase' }}>Billing:</strong>{' '}
                                      {customer.companyAddress || '-'}
                                    </div>
                                    <div className="mt-1" style={{ fontSize: '12.5px' }}>
                                      <strong className="text-muted" style={{ fontSize: '10px', textTransform: 'uppercase' }}>Shipping:</strong>{' '}
                                      {customer.shippingAddress || '-'}
                                    </div>
                                  </td>
                                  <td className="text-end" style={{ verticalAlign: 'top' }}>
                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="fw-semibold px-2 py-1"
                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                        onClick={() => handleOpenEditCustomer(customer)}
                                      >
                                        <i className="fas fa-edit"></i> Edit
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="fw-semibold px-2 py-1"
                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                        onClick={() => handleDeleteCustomer(customer)}
                                      >
                                        <i className="fas fa-trash-alt"></i> Delete
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        <AdminPagination currentPage={customersPage} totalItems={filteredCustomers.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCustomersPage} />
                      </div>
                    )}
                  </div>
                )}

                {/* PRICING TAB */}
                {activeTab === 'pricing' && (
                  <div className="step-content-fade">
                    <h4 className="fw-bold text-dark mb-4">Product Base Price & Options Configuration</h4>

                    {productsLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading products and price configurations...</p>
                      </div>
                    ) : !rawProductsData?.products || rawProductsData.products.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-info-circle mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No products found.</div>
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                          <thead className="table-light">
                            <tr style={{ fontSize: '12px', color: '#64748b' }}>
                              <th className="border-0 py-3">Product Name</th>
                              <th className="border-0 py-3 text-center">Base Price (INR)</th>
                              <th className="border-0 py-3">Options & Choice Markups</th>
                              <th className="border-0 py-3 text-center">Last Modified</th>
                              <th className="border-0 py-3 text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rawProductsData.products.map((prod: any) => (
                              <tr key={prod.productId} className="bg-light-row" style={{ fontSize: '13.5px' }}>
                                <td className="fw-semibold text-dark py-3" style={{ minWidth: '180px' }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="rounded-3 d-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                                      style={{ width: '38px', height: '38px', fontSize: '16px' }}
                                    >
                                      <i className={prod.icon}></i>
                                    </div>
                                    <div>
                                      <div className="fw-bold">{prod.name}</div>
                                      <div className="text-muted font-monospace" style={{ fontSize: '11px' }}>ID: {prod.productId}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="text-center fw-bold text-primary" style={{ fontSize: '15px' }}>
                                  ₹{prod.basePrice}
                                </td>
                                <td style={{ maxWidth: '400px' }}>
                                  <div className="d-flex flex-wrap gap-2">
                                    {prod.options && prod.options.map((opt: any) => {
                                      if (opt.type === 'number') {
                                        return (
                                          <Badge key={opt.name} bg="secondary" className="px-2.5 py-1.5 rounded-3 fw-medium" style={{ fontSize: '11px' }}>
                                            {opt.label}: {opt.min || 0} to {opt.max || 100} {opt.unit}
                                          </Badge>
                                        );
                                      }
                                      return (
                                        <div key={opt.name} className="border bg-white p-1.5 rounded-3 shadow-sm" style={{ fontSize: '11px', minWidth: '150px' }}>
                                          <div className="fw-bold text-dark border-bottom pb-0.5 mb-1">{opt.label}</div>
                                          {opt.choices && opt.choices.map((c: any) => (
                                            <div key={c.label} className="d-flex justify-content-between text-muted py-0.5">
                                              <span>{c.label}</span>
                                              <span className="fw-bold text-success">+{c.extraCost > 0 ? `₹${c.extraCost}` : '₹0'}</span>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                                <td className="text-center text-muted" style={{ fontSize: '12px' }}>
                                  {prod.updatedAt ? new Date(Number(prod.updatedAt) ? Number(prod.updatedAt) : prod.updatedAt).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }) : 'N/A'}
                                </td>
                                <td className="text-end" style={{ minWidth: '130px' }}>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="fw-semibold px-3 py-1.5"
                                    style={{ borderRadius: '6px' }}
                                    onClick={() => handleOpenPricingModal(prod)}
                                  >
                                    <i className="fas fa-edit me-1"></i> Edit Prices
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}

                {/* PRODUCTS TAB */}
                {activeTab === 'products' && (
                  <div className="step-content-fade">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                      <div>
                        <h4 className="fw-bold text-dark mb-1">Product Catalogue Management</h4>
                        <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Add, update, or remove products and configure their visibility on the public site.</p>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Form.Control
                          type="text"
                          placeholder="Search products..."
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          style={{ maxWidth: '240px', borderRadius: '8px', fontSize: '13px' }}
                        />
                        <Button
                          variant="primary"
                          onClick={() => {
                            setEditingProduct(null);
                            setShowProductModal(true);
                          }}
                          className="fw-semibold d-flex align-items-center gap-2 px-3"
                          style={{ borderRadius: '8px', fontSize: '13.5px' }}
                        >
                          <i className="fas fa-plus"></i> Add Product
                        </Button>
                      </div>
                    </div>

                    {productsLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading products...</p>
                      </div>
                    ) : !rawProductsData?.products || rawProductsData.products.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-info-circle mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No products found in the catalogue.</div>
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                          <thead className="table-light">
                            <tr style={{ fontSize: '12px', color: '#64748b' }}>
                              <th className="border-0 py-3">Product Name & ID</th>
                              <th className="border-0 py-3 text-center">Base Price</th>
                              <th className="border-0 py-3">Description</th>
                              <th className="border-0 py-3 text-center">Options Count</th>
                              <th className="border-0 py-3 text-center">Status</th>
                              <th className="border-0 py-3 text-center">Last Modified</th>
                              <th className="border-0 py-3 text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rawProductsData.products
                              .filter((prod: any) => prod.name.toLowerCase().includes(productSearch.toLowerCase()) || prod.productId.toLowerCase().includes(productSearch.toLowerCase()))
                              .map((prod: any) => (
                                <tr key={prod.id} className="bg-light-row" style={{ fontSize: '13.5px' }}>
                                  <td className="fw-semibold text-dark py-3" style={{ minWidth: '180px' }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <div
                                        className="rounded-3 d-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                                        style={{ width: '38px', height: '38px', fontSize: '16px' }}
                                      >
                                        <i className={prod.icon || 'fas fa-print'}></i>
                                      </div>
                                      <div>
                                        <div className="fw-bold">{prod.name}</div>
                                        <div className="text-muted font-monospace" style={{ fontSize: '11px' }}>ID: {prod.productId}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-center fw-bold text-primary" style={{ fontSize: '15px' }}>
                                    ₹{prod.basePrice}
                                  </td>
                                  <td style={{ maxWidth: '300px' }}>
                                    <div className="text-truncate text-muted" title={prod.desc || 'No description'}>
                                      {prod.desc || <span className="text-muted-light">No description provided</span>}
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <Badge bg="secondary" className="rounded-pill px-2.5 py-1 fw-semibold" style={{ fontSize: '11.5px' }}>
                                      {prod.options?.length || 0} Options
                                    </Badge>
                                  </td>
                                  <td className="text-center">
                                    <Form.Check
                                      type="switch"
                                      id={`toggle-product-${prod.id}`}
                                      checked={prod.isActive !== false}
                                      onChange={async (e) => {
                                        const nextActive = e.target.checked;
                                        try {
                                          const { data } = await updateProductMutation({
                                            variables: {
                                              id: prod.id,
                                              isActive: nextActive,
                                            }
                                          });
                                          if (data?.updateProduct?.success) {
                                            triggerToast(data.updateProduct.message || 'Product visibility updated.', 'success');
                                          } else {
                                            triggerToast(data?.updateProduct?.message || 'Failed to update visibility.', 'danger');
                                          }
                                        } catch (err: any) {
                                          triggerToast(err.message || 'Error updating product visibility.', 'danger');
                                        }
                                      }}
                                      style={{ display: 'inline-block' }}
                                    />
                                    <span className={`ms-2 badge rounded-pill ${prod.isActive !== false ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`} style={{ fontSize: '11px' }}>
                                      {prod.isActive !== false ? 'Listed' : 'Hidden'}
                                    </span>
                                  </td>
                                  <td className="text-center text-muted" style={{ fontSize: '12px' }}>
                                    {prod.updatedAt ? new Date(Number(prod.updatedAt) ? Number(prod.updatedAt) : prod.updatedAt).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }) : 'N/A'}
                                  </td>
                                  <td className="text-end" style={{ minWidth: '180px' }}>
                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="fw-semibold px-2 py-1"
                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                        onClick={() => {
                                          setEditingProduct(prod);
                                          setShowProductModal(true);
                                        }}
                                      >
                                        <i className="fas fa-edit"></i> Edit
                                      </Button>
                                      <Button
                                        variant={confirmDeleteId === prod.id ? 'danger' : 'outline-danger'}
                                        size="sm"
                                        className="fw-semibold px-2 py-1"
                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                        onClick={() => handleDeleteProduct(prod.id)}
                                      >
                                        {confirmDeleteId === prod.id ? (
                                          <><i className="fas fa-exclamation-triangle me-1"></i>Confirm?</>
                                        ) : (
                                          <><i className="fas fa-trash-alt"></i> Delete</>
                                        )}
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}

                {/* JOB CARDS TAB */}
                {activeTab === 'jobcards' && (
                  <div className="step-content-fade">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                      <div>
                        <h4 className="fw-bold text-dark mb-1">Job Card Production Management</h4>
                        <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Track digital print jobs through the production workshop workflow</p>
                      </div>
                      <div className="d-flex gap-2 flex-wrap align-items-center">
                        <Form.Control
                          type="text"
                          placeholder="Search ID, customer, specs..."
                          value={jobCardSearch}
                          onChange={(e) => { setJobCardSearch(e.target.value); setJobCardsPage(1); }}
                          style={{ maxWidth: '200px', borderRadius: '8px', fontSize: '13px' }}
                        />
                        <Form.Select
                          value={jobCardStatusFilter}
                          onChange={(e) => { setJobCardStatusFilter(e.target.value); setJobCardsPage(1); }}
                          style={{ width: '130px', borderRadius: '8px', fontSize: '13px' }}
                          className="d-inline"
                        >
                          <option value="all">All Statuses</option>
                          <option value="designing">Designing</option>
                          <option value="pending_setup">Pending Setup</option>
                          <option value="printing">Printing</option>
                          <option value="finishing">Finishing</option>
                          <option value="ready">Ready</option>
                          <option value="completed">Completed</option>
                        </Form.Select>

                        <div className="btn-group border rounded-3 p-0.5 bg-light" style={{ height: '36px' }}>
                          <Button
                            variant={jobCardViewMode === 'kanban' ? 'white' : 'light'}
                            size="sm"
                            onClick={() => setJobCardViewMode('kanban')}
                            className={`px-3 py-1 border-0 fw-semibold ${jobCardViewMode === 'kanban' ? 'shadow-sm text-primary rounded-2 bg-white' : 'text-muted bg-transparent'}`}
                            style={{ fontSize: '12.5px' }}
                          >
                            <i className="fas fa-columns me-1"></i> Kanban
                          </Button>
                          <Button
                            variant={jobCardViewMode === 'table' ? 'white' : 'light'}
                            size="sm"
                            onClick={() => setJobCardViewMode('table')}
                            className={`px-3 py-1 border-0 fw-semibold ${jobCardViewMode === 'table' ? 'shadow-sm text-primary rounded-2 bg-white' : 'text-muted bg-transparent'}`}
                            style={{ fontSize: '12.5px' }}
                          >
                            <i className="fas fa-list me-1"></i> List Table
                          </Button>
                        </div>

                        <div className="btn-group border rounded-3 p-0.5 bg-light ms-2" style={{ height: '36px' }}>
                          <Button
                            variant="light"
                            size="sm"
                            onClick={handleExportJobCardsExcel}
                            className="px-3 py-1 border-0 fw-semibold text-muted bg-transparent"
                            style={{ fontSize: '12.5px' }}
                            title="Export filtered job cards to Excel"
                          >
                            <i className="fas fa-file-excel text-success me-1"></i> Excel
                          </Button>
                          <Button
                            variant="light"
                            size="sm"
                            onClick={handleExportJobCardsPDF}
                            className="px-3 py-1 border-0 fw-semibold text-muted bg-transparent"
                            style={{ fontSize: '12.5px' }}
                            title="Export filtered job cards to PDF"
                          >
                            <i className="fas fa-file-pdf text-danger me-1"></i> PDF
                          </Button>
                        </div>
                      </div>
                    </div>

                    {jobCardsLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading workshop job cards...</p>
                      </div>
                    ) : jobCardsList.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-print mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No Job Cards generated yet. Go to the "Orders" tab to convert confirmed orders to job cards!</div>
                      </Alert>
                    ) : (
                      <>
                        {/* TABLE VIEW */}
                        {jobCardViewMode === 'table' && (
                          <div className="table-responsive">
                            <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                              <thead className="table-light">
                                <tr style={{ fontSize: '12px', color: '#64748b' }}>
                                  <th className="border-0 py-3">Job ID</th>
                                  <th className="border-0 py-3">Order Ref</th>
                                  <th className="border-0 py-3">Customer</th>
                                  <th className="border-0 py-3">Priority / Status</th>
                                  <th className="border-0 py-3">Production Assigns</th>
                                  <th className="border-0 py-3">Last Updated</th>
                                  <th className="border-0 py-3 text-end">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredJobCards
                                  .slice((jobCardsPage - 1) * ITEMS_PER_PAGE, jobCardsPage * ITEMS_PER_PAGE)
                                  .map((jc: any) => (
                                    <tr key={jc.id} className="bg-light-row" style={{ fontSize: '13.5px' }}>
                                      <td className="fw-semibold text-dark font-monospace py-3">{jc.jobCardId}</td>
                                      <td>
                                        <Badge bg="light" className="text-dark border font-monospace px-2 py-1">{jc.orderId}</Badge>
                                      </td>
                                      <td>
                                        <div className="fw-bold text-dark">{jc.customerName}</div>
                                        {jc.customerId && <div className="text-muted font-monospace mt-1" style={{ fontSize: '10.5px' }}><i className="fas fa-id-card me-1"></i>{jc.customerId}</div>}
                                        <div className="text-muted" style={{ fontSize: '11px' }}>{jc.customerPhone}</div>
                                      </td>
                                      <td>
                                        <div className="d-flex flex-column gap-1">
                                          <div>
                                            {jc.priority === 'urgent' && <Badge bg="danger" className="rounded-pill px-2.5">Urgent</Badge>}
                                            {jc.priority === 'high' && <Badge bg="warning" text="dark" className="rounded-pill px-2.5">High</Badge>}
                                            {jc.priority === 'medium' && <Badge bg="info" className="rounded-pill px-2.5">Medium</Badge>}
                                            {jc.priority === 'low' && <Badge bg="secondary" className="rounded-pill px-2.5">Low</Badge>}
                                          </div>
                                          <div>
                                            <Badge bg="dark" className="px-2.5 py-1 text-capitalize" style={{ fontSize: '10.5px' }}>
                                              {jc.status.replace('_', ' ')}
                                            </Badge>
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                                          {jc.operator ? <div><strong>Op:</strong> {jc.operator}</div> : <div className="text-muted italic">No operator</div>}
                                          {jc.designer ? <div><strong>Design:</strong> {jc.designer}</div> : null}
                                          {jc.machine ? <div><strong>Machine:</strong> {jc.machine}</div> : null}
                                          {jc.material ? <div><strong>Material:</strong> {jc.material}</div> : null}
                                        </div>
                                      </td>
                                      <td>
                                        <div style={{ fontSize: '12px' }}>
                                          <div><strong>By:</strong> {jc.updatedBy || 'System'}</div>
                                          <div className="text-muted">{new Date(Number(jc.updatedAt) || jc.updatedAt).toLocaleDateString()} {new Date(Number(jc.updatedAt) || jc.updatedAt).toLocaleTimeString()}</div>
                                        </div>
                                      </td>
                                      <td className="text-end">
                                        <div className="d-flex align-items-center justify-content-end gap-1">
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleOpenTicketModal(jc)}
                                            title="Print Job Ticket"
                                          >
                                            <i className="fas fa-print"></i>
                                          </Button>
                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleOpenEditJobCard(jc)}
                                            title="Edit Job Card"
                                          >
                                            <i className="fas fa-edit"></i>
                                          </Button>
                                          {jc.status === 'completed' && (
                                            <Button
                                              variant="outline-info"
                                              size="sm"
                                              onClick={() => handleGenerateInvoice(jc)}
                                              title="Generate Invoice"
                                            >
                                              <i className="fas fa-file-invoice-dollar"></i>
                                            </Button>
                                          )}
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteJobCard(jc)}
                                            title="Delete Job Card"
                                          >
                                            <i className="fas fa-trash-alt"></i>
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                            <AdminPagination currentPage={jobCardsPage} totalItems={filteredJobCards.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setJobCardsPage} />
                          </div>
                        )}

                        {/* KANBAN BOARD VIEW */}
                        {jobCardViewMode === 'kanban' && (
                          <div className="kanban-board-wrapper mt-2">
                            <Row className="g-3 flex-nowrap overflow-auto pb-3" style={{ minWidth: '1000px' }}>
                              {[
                                { id: 'designing', title: 'Designing', bg: '#fdf4ff', border: '#f5d0fe', icon: 'fas fa-paint-brush text-purple' },
                                { id: 'pending_setup', title: 'Pending Setup', bg: '#f8fafc', border: '#e2e8f0', icon: 'fas fa-cog text-muted' },
                                { id: 'printing', title: 'Printing', bg: '#f0fdf4', border: '#bbf7d0', icon: 'fas fa-print text-success' },
                                { id: 'finishing', title: 'Finishing / Cutting', bg: '#eff6ff', border: '#bfdbfe', icon: 'fas fa-cut text-primary' },
                                { id: 'ready', title: 'Ready for Dispatch', bg: '#fffbeb', border: '#fef3c7', icon: 'fas fa-box-open text-warning' },
                                { id: 'completed', title: 'Completed', bg: '#faf5ff', border: '#e9d5ff', icon: 'fas fa-check-circle text-purple' },
                              ].map((col) => {
                                const colJobs = jobCardsList
                                  .filter((jc: any) => jc.status === col.id)
                                  .filter((jc: any) => {
                                    return jc.jobCardId.toLowerCase().includes(jobCardSearch.toLowerCase()) ||
                                      (jc.customerId && jc.customerId.toLowerCase().includes(jobCardSearch.toLowerCase())) ||
                                      jc.customerName.toLowerCase().includes(jobCardSearch.toLowerCase()) ||
                                      jc.operator.toLowerCase().includes(jobCardSearch.toLowerCase()) ||
                                      (jc.designer && jc.designer.toLowerCase().includes(jobCardSearch.toLowerCase())) ||
                                      jc.items.some((i: any) => i.productName.toLowerCase().includes(jobCardSearch.toLowerCase()));
                                  });

                                return (
                                  <Col key={col.id} style={{ minWidth: '220px', maxWidth: '300px' }}>
                                    <div
                                      className="rounded-4 p-3 border-top border-4 h-100 shadow-sm"
                                      style={{ backgroundColor: col.bg, borderTopColor: col.border, borderLeft: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', minHeight: '550px' }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                          <i className={col.icon}></i>
                                          <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '13px' }}>{col.title}</h6>
                                        </div>
                                        <Badge bg="secondary" className="rounded-pill" style={{ fontSize: '11px' }}>{colJobs.length}</Badge>
                                      </div>

                                      <div className="d-flex flex-column gap-2" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                        {colJobs.map((jc: any) => {
                                          let priorityColor = '#cbd5e1';
                                          if (jc.priority === 'urgent') priorityColor = 'var(--red)';
                                          else if (jc.priority === 'high') priorityColor = 'var(--pink)';
                                          else if (jc.priority === 'medium') priorityColor = 'var(--cyan)';

                                          return (
                                            <Card
                                              key={jc.id}
                                              className="border-0 shadow-sm rounded-3 p-3 bg-white hover-lift-mini"
                                              style={{ borderLeft: `4px solid ${priorityColor}` }}
                                            >
                                              <div className="d-flex justify-content-between align-items-start mb-2">
                                                <span className="font-monospace fw-bold text-dark" style={{ fontSize: '11px' }}>{jc.jobCardId}</span>
                                                <span style={{ fontSize: '10px' }} className="text-muted">
                                                  {new Date(Number(jc.createdAt) ? Number(jc.createdAt) : jc.createdAt).toLocaleDateString()}
                                                </span>
                                              </div>

                                              <div>
                                                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '12px' }}>{jc.customerName}</h6>
                                                {jc.customerId && <div className="text-muted font-monospace mt-1" style={{ fontSize: '10px' }}><i className="fas fa-id-card me-1"></i>{jc.customerId}</div>}
                                              </div>

                                              <div className="mb-2 border-bottom pb-2">
                                                {jc.items && jc.items.map((item: any, idx: number) => (
                                                  <div key={idx} style={{ fontSize: '11px', lineHeight: '1.3' }} className="text-muted">
                                                    {item.quantity}x <strong>{item.productName}</strong>
                                                    <div className="text-capitalize text-muted font-monospace" style={{ fontSize: '9.5px', paddingLeft: '6px' }}>
                                                      {item.options && item.options.map((opt: any) => (
                                                        <div key={opt.key}>{opt.key.replace(/([A-Z])/g, ' $1')}: {opt.value}</div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>

                                              <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: '10.5px' }}>
                                                <div className="d-flex align-items-center gap-2">
                                                  {jc.operator ? <span><i className="far fa-user me-1"></i>{jc.operator}</span> : <span className="italic text-muted">No operator</span>}
                                                  {jc.designer ? <span><i className="fas fa-paint-brush me-1 text-purple"></i>{jc.designer}</span> : null}
                                                </div>
                                                {jc.machine && (
                                                  <span className="badge bg-light text-dark border rounded px-1.5 py-0.5">
                                                    <i className="fas fa-print me-1" style={{ fontSize: '9px' }}></i>{jc.machine}
                                                  </span>
                                                )}
                                              </div>

                                              <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                                <div className="d-flex gap-1">
                                                  <Button
                                                    variant="link"
                                                    className="text-muted p-0 me-1"
                                                    onClick={() => handleOpenTicketModal(jc)}
                                                    title="Print Job Ticket"
                                                  >
                                                    <i className="fas fa-print" style={{ fontSize: '12px' }}></i>
                                                  </Button>
                                                  <Button
                                                    variant="link"
                                                    className="text-primary p-0 me-1"
                                                    onClick={() => handleOpenEditJobCard(jc)}
                                                    title="Edit details"
                                                  >
                                                    <i className="fas fa-edit" style={{ fontSize: '12px' }}></i>
                                                  </Button>
                                                  <Button
                                                    variant="link"
                                                    className="text-danger p-0"
                                                    onClick={() => handleDeleteJobCard(jc)}
                                                    title="Delete Job"
                                                  >
                                                    <i className="fas fa-trash-alt" style={{ fontSize: '11px' }}></i>
                                                  </Button>
                                                </div>

                                                <div className="d-flex gap-1">
                                                  {col.id === 'completed' && (
                                                    <Button
                                                      variant="outline-primary"
                                                      size="sm"
                                                      className="fw-bold px-2 py-1 d-flex align-items-center justify-content-center"
                                                      style={{ borderRadius: '4px' }}
                                                      onClick={() => handleGenerateInvoice(jc)}
                                                      title="Generate Invoice"
                                                    >
                                                      <i className="fas fa-file-invoice-dollar" style={{ fontSize: '11px' }}></i>
                                                    </Button>
                                                  )}
                                                  {col.id !== 'designing' && (
                                                    <Button
                                                      variant="outline-warning"
                                                      size="sm"
                                                      className="fw-bold px-2 py-1 d-flex align-items-center justify-content-center"
                                                      style={{ borderRadius: '4px' }}
                                                      onClick={() => handleRevertJobCardStatus(jc)}
                                                      title="Move to Previous Stage"
                                                    >
                                                      <i className="fas fa-arrow-left" style={{ fontSize: '11px' }}></i>
                                                    </Button>
                                                  )}
                                                  {col.id !== 'completed' && (
                                                    <Button
                                                      variant="outline-success"
                                                      size="sm"
                                                      className="fw-bold px-2 py-1 d-flex align-items-center justify-content-center"
                                                      style={{ borderRadius: '4px' }}
                                                      onClick={() => handleAdvanceJobCardStatus(jc)}
                                                      title="Move to Next Stage"
                                                    >
                                                      <i className="fas fa-arrow-right" style={{ fontSize: '11px' }}></i>
                                                    </Button>
                                                  )}
                                                </div>
                                              </div>
                                            </Card>
                                          );
                                        })}
                                        {colJobs.length === 0 && (
                                          <div className="text-center py-4 text-muted border border-dashed rounded-3" style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                                            No jobs in this stage
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* INVENTORY TAB */}
                {activeTab === 'inventory' && (
                  <div className="step-content-fade">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                      <h4 className="fw-bold text-dark mb-0">Inventory Stock Management</h4>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="outline-success"
                          onClick={handleExportExcel}
                          className="d-flex align-items-center gap-1 fw-semibold btn-sm"
                          style={{ borderRadius: '8px' }}
                          disabled={filteredInventory.length === 0}
                        >
                          <i className="fas fa-file-excel"></i> Export Excel
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={handleExportPDF}
                          className="d-flex align-items-center gap-1 fw-semibold btn-sm"
                          style={{ borderRadius: '8px' }}
                          disabled={filteredInventory.length === 0}
                        >
                          <i className="fas fa-file-pdf"></i> Export PDF
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleOpenAddInventory}
                          className="d-flex align-items-center gap-1 fw-semibold btn-sm px-3"
                          style={{ borderRadius: '8px' }}
                        >
                          <i className="fas fa-plus"></i> Add Item
                        </Button>
                      </div>
                    </div>

                    {/* Filter and Search Bar */}
                    <div className="bg-light p-3 rounded-3 mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                      <div className="d-flex gap-2 flex-wrap align-items-center">
                        <Form.Control
                          type="text"
                          placeholder="Search item name, supplier..."
                          value={inventorySearch}
                          onChange={(e) => { setInventorySearch(e.target.value); setInventoryPage(1); }}
                          style={{ width: '280px', borderRadius: '8px', fontSize: '13px' }}
                        />
                        <Form.Select
                          value={inventoryCategoryFilter}
                          onChange={(e) => { setInventoryCategoryFilter(e.target.value); setInventoryPage(1); }}
                          style={{ width: '200px', borderRadius: '8px', fontSize: '13px' }}
                        >
                          <option value="all">All Categories</option>
                          <option value="Banner Printing">Banner Printing</option>
                          <option value="Photo frames printing">Photo frames printing</option>
                          <option value="Digital printings">Digital printings</option>
                        </Form.Select>
                      </div>

                      {/* Status Indicators */}
                      <div className="d-flex gap-3 text-muted" style={{ fontSize: '12px' }}>
                        <div>
                          <Badge bg="success" className="me-1 rounded-circle" style={{ width: '10px', height: '10px', display: 'inline-block', padding: 0, verticalAlign: 'middle' }}></Badge>
                          In Stock: {filteredInventory.filter(isInStockItem).length}
                        </div>
                        <div>
                          <Badge bg="warning" text="dark" className="me-1 rounded-circle" style={{ width: '10px', height: '10px', display: 'inline-block', padding: 0, verticalAlign: 'middle' }}></Badge>
                          Low Stock: {filteredInventory.filter(isLowStockItem).length}
                        </div>
                        <div>
                          <Badge bg="danger" className="me-1 rounded-circle" style={{ width: '10px', height: '10px', display: 'inline-block', padding: 0, verticalAlign: 'middle' }}></Badge>
                          Out of Stock: {filteredInventory.filter(isOutOfStockItem).length}
                        </div>
                      </div>
                    </div>

                    {inventoryLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading inventory...</p>
                      </div>
                    ) : filteredInventory.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-boxes mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No inventory items found.</div>
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                          <thead className="table-light">
                            <tr style={{ fontSize: '12px', color: '#64748b' }}>
                              <th className="border-0 py-3">Item Name</th>
                              <th className="border-0 py-3">Category</th>
                              <th className="border-0 py-3">Stock Level</th>
                              <th className="border-0 py-3">Unit</th>
                              <th className="border-0 py-3">Supplier</th>
                              <th className="border-0 py-3">Order Date</th>
                              <th className="border-0 py-3">Last Updated</th>
                              <th className="border-0 py-3 text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredInventory.slice((inventoryPage - 1) * ITEMS_PER_PAGE, inventoryPage * ITEMS_PER_PAGE).map((item: any) => {
                              const isOutOfStock = isOutOfStockItem(item);
                              const isLowStock = isLowStockItem(item);
                              let statusBadge = <Badge bg="success" className="rounded-pill px-2.5 py-1">In Stock</Badge>;
                              let rowClass = 'bg-light-row';

                              if (isOutOfStock) {
                                statusBadge = <Badge bg="danger" className="rounded-pill px-2.5 py-1">Out of Stock</Badge>;
                                rowClass = 'bg-light-row border-start border-danger border-3';
                              } else if (isLowStock) {
                                statusBadge = <Badge bg="warning" text="dark" className="rounded-pill px-2.5 py-1">Low Stock</Badge>;
                                rowClass = 'bg-light-row border-start border-warning border-3';
                              }

                              return (
                                <tr key={item.id} className={rowClass} style={{ fontSize: '13px' }}>
                                  <td className="fw-semibold text-dark py-3">
                                    <div>{item.name}</div>
                                    {item.category === 'Banner Printing' && (item.bannerType || item.bannerSize) && (
                                      <div className="fw-semibold mt-1" style={{ fontSize: '11px' }}>
                                        {item.bannerType && (
                                          <Badge bg="info" className="me-1 px-2 py-0.5 text-white" style={{ fontSize: '10px' }}>
                                            {item.bannerType}
                                          </Badge>
                                        )}
                                        {item.bannerSize && (
                                          <Badge bg="secondary" className="px-2 py-0.5" style={{ fontSize: '10px' }}>
                                            {item.bannerSize}
                                          </Badge>
                                        )}
                                      </div>
                                    )}
                                    {item.notes && <div className="text-muted fw-normal mt-1" style={{ fontSize: '11px' }}>{item.notes}</div>}
                                  </td>
                                  <td className="text-muted">{item.category}</td>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      {item.category === 'Banner Printing' ? (
                                        <>
                                          <span className={`fw-bold ${isOutOfStock ? 'text-danger' : isLowStock ? 'text-warning' : 'text-dark'}`}>
                                            {item.overallLength !== undefined ? Number(item.overallLength).toFixed(1) : 0} m
                                          </span>
                                          <span className="text-muted" style={{ fontSize: '11px' }}>
                                            / limit {item.rollLengthLimit || 0} m ({item.stock} {item.unit})
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span className={`fw-bold ${isOutOfStock ? 'text-danger' : isLowStock ? 'text-warning' : 'text-dark'}`}>
                                            {item.stock}
                                          </span>
                                          <span className="text-muted" style={{ fontSize: '11px' }}>
                                            / min {item.minStock}
                                          </span>
                                        </>
                                      )}
                                      <div className="ms-2">{statusBadge}</div>
                                    </div>
                                  </td>
                                  <td className="text-muted text-lowercase">{item.unit}</td>
                                  <td>{item.supplier || <span className="text-muted">—</span>}</td>
                                  <td className="text-muted">
                                    {item.orderDate ? new Date(item.orderDate).toLocaleDateString() : <span className="text-muted">—</span>}
                                  </td>
                                  <td className="text-muted">
                                    {new Date(Number(item.updatedAt) ? Number(item.updatedAt) : item.updatedAt).toLocaleDateString()}
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        style={{ borderRadius: '6px', padding: '4px 8px' }}
                                        onClick={() => handleOpenEditInventory(item)}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        style={{ borderRadius: '6px', padding: '4px 8px' }}
                                        onClick={() => handleDeleteInventory(item)}
                                      >
                                        <i className="fas fa-trash-alt"></i>
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        <AdminPagination currentPage={inventoryPage} totalItems={filteredInventory.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setInventoryPage} />
                      </div>
                    )}
                  </div>
                )}

                {/* ATTENDANCE TAB */}
                {activeTab === 'attendance' && (
                  <div className="step-content-fade">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                      <h4 className="fw-bold text-dark mb-0">Staff Attendance</h4>
                      <div className="bg-light p-1 rounded-3 d-flex border gap-1">
                        <Button
                          variant={attendanceViewMode === 'daily' ? 'primary' : 'light'}
                          size="sm"
                          className="fw-semibold px-3 py-1.5"
                          style={{ borderRadius: '6px' }}
                          onClick={() => setAttendanceViewMode('daily')}
                        >
                          <i className="fas fa-calendar-day me-1"></i> Daily Register
                        </Button>
                        <Button
                          variant={attendanceViewMode === 'all-employees' ? 'primary' : 'light'}
                          size="sm"
                          className="fw-semibold px-3 py-1.5"
                          style={{ borderRadius: '6px' }}
                          onClick={() => setAttendanceViewMode('all-employees')}
                        >
                          <i className="fas fa-users me-1"></i> All Employees Summary
                        </Button>
                      </div>
                    </div>

                    {attendanceViewMode === 'daily' ? (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-3 bg-white p-3 rounded-4 border shadow-sm flex-wrap gap-3">
                          <div className="d-flex gap-2 align-items-center flex-wrap">
                            <Form.Label className="mb-0 fw-semibold text-muted" style={{ fontSize: '13px' }}>Select Date:</Form.Label>
                            <Form.Control
                              type="date"
                              value={attendanceDate}
                              onChange={(e) => { setAttendanceDate(e.target.value); setAttendanceDailyPage(1); }}
                              style={{ width: '160px', borderRadius: '8px', fontSize: '13px' }}
                            />
                          </div>
                          <Button
                            variant="outline-primary"
                            className="fw-semibold d-flex align-items-center gap-1 btn-sm px-3"
                            style={{ borderRadius: '8px', height: '36px' }}
                            onClick={handleMarkAllPresent}
                            disabled={staffAttendanceLoading || (staffAttendanceData?.staffAttendance || []).length === 0}
                          >
                            <i className="fas fa-check-double"></i> Mark All Present
                          </Button>
                        </div>

                        {staffAttendanceLoading ? (
                          <div className="text-center py-5 text-muted">
                            <Spinner animation="border" variant="primary" className="mb-2" />
                            <p>Loading attendance logs...</p>
                          </div>
                        ) : (staffAttendanceData?.staffAttendance || []).length === 0 ? (
                          <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                            <i className="fas fa-user-friends mb-2" style={{ fontSize: '24px' }}></i>
                            <div>No staff members found. Add user accounts with staff/manager/operator roles first.</div>
                          </Alert>
                        ) : (
                          <div className="table-responsive">
                            <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                              <thead className="table-light">
                                <tr style={{ fontSize: '12px', color: '#64748b' }}>
                                  <th className="border-0 py-3">Staff Member</th>
                                  <th className="border-0 py-3">Role</th>
                                  <th className="border-0 py-3">Attendance Status</th>
                                  <th className="border-0 py-3">Check-in Time</th>
                                  <th className="border-0 py-3">Check-out Time</th>
                                  <th className="border-0 py-3">Location</th>
                                  <th className="border-0 py-3">Notes</th>
                                  <th className="border-0 py-3 text-end">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(staffAttendanceData?.staffAttendance || []).slice((attendanceDailyPage - 1) * ITEMS_PER_PAGE, attendanceDailyPage * ITEMS_PER_PAGE).map((item: any) => {
                                  const user = item.user;
                                  const att = item.attendance || {
                                    status: 'present',
                                    checkIn: '09:00',
                                    checkOut: '18:00',
                                    notes: '',
                                  };

                                  return (
                                    <AttendanceRow
                                      key={user.id}
                                      user={user}
                                      initialAttendance={att}
                                      date={attendanceDate}
                                      onSave={handleMarkAttendance}
                                      onViewSummary={(u: any) => {
                                        setSelectedAttendanceUser(u);
                                        setShowAttendanceSummaryModal(true);
                                      }}
                                    />
                                  );
                                })}
                              </tbody>
                            </Table>
                            <AdminPagination currentPage={attendanceDailyPage} totalItems={(staffAttendanceData?.staffAttendance || []).length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setAttendanceDailyPage} />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-3 bg-white p-3 rounded-4 border shadow-sm flex-wrap gap-3">
                          <div className="d-flex gap-3 align-items-center flex-wrap">
                            <div className="d-flex align-items-center gap-2">
                              <Form.Label className="mb-0 fw-semibold text-muted" style={{ fontSize: '13px' }}>Start Date:</Form.Label>
                              <Form.Control
                                type="date"
                                value={allEmployeesAttendanceStart}
                                onChange={(e) => { setAllEmployeesAttendanceStart(e.target.value); setAttendanceSummaryPage(1); }}
                                style={{ width: '150px', borderRadius: '8px', fontSize: '13px' }}
                              />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Form.Label className="mb-0 fw-semibold text-muted" style={{ fontSize: '13px' }}>End Date:</Form.Label>
                              <Form.Control
                                type="date"
                                value={allEmployeesAttendanceEnd}
                                onChange={(e) => { setAllEmployeesAttendanceEnd(e.target.value); setAttendanceSummaryPage(1); }}
                                style={{ width: '150px', borderRadius: '8px', fontSize: '13px' }}
                              />
                            </div>
                          </div>
                          <Button
                            variant="outline-primary"
                            className="fw-semibold d-flex align-items-center gap-1 btn-sm px-3"
                            style={{ borderRadius: '8px', height: '36px' }}
                            onClick={() => refetchAllStaffSummary()}
                            disabled={allStaffSummaryLoading}
                          >
                            <i className="fas fa-sync-alt"></i> Reload Report
                          </Button>
                        </div>

                        {allStaffSummaryLoading ? (
                          <div className="text-center py-5 text-muted">
                            <Spinner animation="border" variant="primary" className="mb-2" />
                            <p>Loading summary report...</p>
                          </div>
                        ) : (allStaffSummaryData?.allStaffAttendanceSummary || []).length === 0 ? (
                          <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                            <i className="fas fa-user-friends mb-2" style={{ fontSize: '24px' }}></i>
                            <div>No staff members found. Add user accounts with staff/manager/operator roles first.</div>
                          </Alert>
                        ) : (
                          <div className="table-responsive">
                            <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                              <thead className="table-light">
                                <tr style={{ fontSize: '12px', color: '#64748b' }}>
                                  <th className="border-0 py-3">Employee Name</th>
                                  <th className="border-0 py-3">Role</th>
                                  <th className="border-0 py-3 text-center">Days Present</th>
                                  <th className="border-0 py-3 text-center">Days Absent</th>
                                  <th className="border-0 py-3 text-center">Half Days</th>
                                  <th className="border-0 py-3 text-center">Leaves</th>
                                  <th className="border-0 py-3 text-center">Total Days</th>
                                  <th className="border-0 py-3 text-center">Attendance Rate</th>
                                  <th className="border-0 py-3 text-end">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(allStaffSummaryData?.allStaffAttendanceSummary || []).slice((attendanceSummaryPage - 1) * ITEMS_PER_PAGE, attendanceSummaryPage * ITEMS_PER_PAGE).map((item: any) => {
                                  const { user, summary } = item;
                                  const total = summary.presentCount + summary.absentCount + summary.halfDayCount + summary.leaveCount;
                                  const attendanceRate = total > 0
                                    ? Math.round(((summary.presentCount + (summary.halfDayCount * 0.5)) / total) * 100)
                                    : 0;

                                  let rateColor = 'text-success';
                                  if (attendanceRate < 75) rateColor = 'text-danger';
                                  else if (attendanceRate < 90) rateColor = 'text-warning';

                                  return (
                                    <tr key={user.id} className="bg-light-row" style={{ fontSize: '13px' }}>
                                      <td className="fw-semibold text-dark py-3">
                                        {user.username}
                                      </td>
                                      <td className="text-muted text-capitalize">
                                        {user.role.replace('_', ' ')}
                                      </td>
                                      <td className="text-center fw-semibold text-success">{summary.presentCount}</td>
                                      <td className="text-center fw-semibold text-danger">{summary.absentCount}</td>
                                      <td className="text-center fw-semibold text-warning">{summary.halfDayCount}</td>
                                      <td className="text-center fw-semibold text-info">{summary.leaveCount}</td>
                                      <td className="text-center text-secondary">{total}</td>
                                      <td className="text-center">
                                        <span className={`fw-bold ${rateColor}`}>{attendanceRate}%</span>
                                      </td>
                                      <td className="text-end">
                                        <Button
                                          variant="outline-info"
                                          size="sm"
                                          className="fw-semibold"
                                          style={{ borderRadius: '6px' }}
                                          onClick={() => {
                                            setSelectedAttendanceUser(user);
                                            setAttendanceSummaryStart(allEmployeesAttendanceStart);
                                            setAttendanceSummaryEnd(allEmployeesAttendanceEnd);
                                            setShowAttendanceSummaryModal(true);
                                          }}
                                          title="View Detailed Logs"
                                        >
                                          <i className="fas fa-history me-1"></i> Logs
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                            <AdminPagination currentPage={attendanceSummaryPage} totalItems={(allStaffSummaryData?.allStaffAttendanceSummary || []).length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setAttendanceSummaryPage} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* PAYSLIPS TAB */}
                {activeTab === 'payslips' && (
                  <div className="step-content-fade">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                      <div>
                        <h4 className="fw-bold text-dark mb-1">Payroll & Payslip Management</h4>
                        <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Generate and manage monthly salary payslips for staff members</p>
                      </div>
                      <Button
                        variant="primary"
                        className="fw-semibold d-flex align-items-center gap-2"
                        style={{ borderRadius: '8px', padding: '10px 18px', fontSize: '13px' }}
                        onClick={() => setShowPayslipModal(true)}
                      >
                        <i className="fas fa-plus"></i> Generate Payslip
                      </Button>
                    </div>

                    {/* Filters */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-4">
                        <Form.Control
                          type="text"
                          placeholder="Search Employee..."
                          value={payslipSearch}
                          onChange={(e) => { setPayslipSearch(e.target.value); setPayslipsPage(1); }}
                          style={{ borderRadius: '8px', fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Select
                          value={payslipMonthFilter}
                          onChange={(e) => { setPayslipMonthFilter(e.target.value); setPayslipsPage(1); }}
                          style={{ borderRadius: '8px', fontSize: '13px' }}
                        >
                          <option value="all">All Months</option>
                          {months.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                          ))}
                        </Form.Select>
                      </div>
                      <div className="col-md-4">
                        <Form.Select
                          value={payslipYearFilter}
                          onChange={(e) => { setPayslipYearFilter(e.target.value); setPayslipsPage(1); }}
                          style={{ borderRadius: '8px', fontSize: '13px' }}
                        >
                          <option value="all">All Years</option>
                          {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </Form.Select>
                      </div>
                    </div>

                    {payslipsLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p>Loading payslip records...</p>
                      </div>
                    ) : filteredPayslips.length === 0 ? (
                      <Alert variant="info" className="text-center rounded-3 py-4 border-0">
                        <i className="fas fa-money-check-alt mb-2" style={{ fontSize: '24px' }}></i>
                        <div>No payslips found matching the filters. Generate a new payslip to get started!</div>
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                          <thead className="table-light">
                            <tr style={{ fontSize: '12px', color: '#64748b' }}>
                              <th className="border-0 py-3">Employee</th>
                              <th className="border-0 py-3">Period</th>
                              <th className="border-0 py-3">Base Salary</th>
                              <th className="border-0 py-3">Net Salary</th>
                              <th className="border-0 py-3">Attendance</th>
                              <th className="border-0 py-3">Payment Mode</th>
                              <th className="border-0 py-3">Status</th>
                              <th className="border-0 py-3 text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredPayslips.slice((payslipsPage - 1) * ITEMS_PER_PAGE, payslipsPage * ITEMS_PER_PAGE).map((p: any) => {
                              const isConfirming = confirmDeleteId === p.id;
                              return (
                                <tr key={p.id} className="bg-light-row" style={{ fontSize: '13px' }}>
                                  <td className="py-3">
                                    <div className="d-flex align-items-center gap-2">
                                      <div
                                        className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary rounded-circle fw-bold"
                                        style={{ width: '32px', height: '32px', fontSize: '12px' }}
                                      >
                                        {p.user?.username?.charAt(0).toUpperCase()}
                                      </div>
                                      <div>
                                        <span className="fw-semibold text-dark d-block">{p.user?.username || 'N/A'}</span>
                                        <span className="text-muted text-capitalize" style={{ fontSize: '11px' }}>{p.user?.role || 'Staff'}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>{months.find((m) => m.value === p.month)?.label} {p.year}</td>
                                  <td>₹{p.baseSalary?.toLocaleString('en-IN')}</td>
                                  <td className="fw-bold text-primary">₹{p.netSalary?.toLocaleString('en-IN')}</td>
                                  <td>
                                    <span className="badge bg-light text-dark border">
                                      {p.presentDays} / {p.workingDays} days
                                    </span>
                                  </td>
                                  <td>{p.paymentMode || 'Bank Transfer'}</td>
                                  <td>
                                    <Badge
                                      bg={p.status === 'Paid' ? 'success-subtle' : 'warning-subtle'}
                                      className={`text-${p.status === 'Paid' ? 'success' : 'warning-emphasis'} px-3 py-1.5 rounded-pill text-capitalize cursor-pointer`}
                                      style={{ fontSize: '11px', fontWeight: 600 }}
                                      onClick={() => handleTogglePayslipStatus(p.id, p.status)}
                                    >
                                      {p.status}
                                    </Badge>
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => downloadPayslipPDF(p)}
                                        className="fw-semibold px-2 py-1"
                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                      >
                                        <i className="fas fa-file-pdf"></i> PDF
                                      </Button>
                                      <Button
                                        variant={isConfirming ? 'danger' : 'outline-danger'}
                                        size="sm"
                                        onClick={() => handleDeletePayslip(p.id)}
                                        className="fw-semibold px-2 py-1"
                                        style={{ borderRadius: '6px', fontSize: '12px' }}
                                      >
                                        {isConfirming ? (
                                          <><i className="fas fa-check"></i> Confirm?</>
                                        ) : (
                                          <><i className="fas fa-trash-alt"></i> Delete</>
                                        )}
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        <AdminPagination currentPage={payslipsPage} totalItems={filteredPayslips.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setPayslipsPage} />
                      </div>
                    )}
                  </div>
                )}

                {/* ESTIMATION TAB */}
                {activeTab === 'estimation' && (
                  <div className="step-content-fade">
                    <EstimationGenerator
                      products={rawProductsData?.products || []}
                      users={usersData?.users || []}
                      triggerToast={triggerToast}
                    />
                  </div>
                )}

                {/* INVOICES TAB */}
                {activeTab === 'invoices' && (
                  <div className="step-content-fade">
                    <InvoiceGenerator
                      products={rawProductsData?.products || []}
                      users={usersData?.users || []}
                    />
                  </div>
                )}

                {/* SALES TAB */}
                {activeTab === 'sales' && (
                  <div className="step-content-fade">
                    <SalesTracker triggerToast={triggerToast} />
                  </div>
                )}

                {/* PURCHASES TAB */}
                {activeTab === 'purchase' && (
                  <div className="step-content-fade">
                    <PurchaseTracker triggerToast={triggerToast} />
                  </div>
                )}

                {/* EXPENSES TAB */}
                {activeTab === 'expenses' && (
                  <div className="step-content-fade">
                    <ExpenseTracker triggerToast={triggerToast} />
                  </div>
                )}

                {/* BANK STATEMENT ANALYZER TAB */}
                {activeTab === 'bankstatement' && (
                  <div className="step-content-fade">
                    <BankStatementAnalyzer
                      triggerToast={triggerToast}
                      onNavigateToTab={(tab) => setActiveTab(tab as any)}
                    />
                  </div>
                )}

                {/* TASKS TAB */}
                {activeTab === 'tasks' && (
                  <div className="step-content-fade">
                    <TaskManager triggerToast={triggerToast} />
                  </div>
                )}


              </Card>
            </div>
          </div>
        </Container>
      </div>

      <OrderDetailsModal
        show={showOrderModal}
        onHide={() => {
          setShowOrderModal(false);
          setIsEditOrderMode(false);
        }}
        selectedOrder={selectedOrder}
        updatingOrderStatusId={updatingOrderStatusId}
        jobCardsList={jobCardsList}
        onOpenTicket={handleOpenTicketModal}
        onOpenCreateJobCard={handleOpenCreateJobCard}
        onUpdateStatus={handleUpdateStatus}
        onUpdateOrder={handleUpdateOrder}
        isInitialEditMode={isEditOrderMode}
      />

      <UserModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        isEditingUser={isEditingUser}
        userForm={userForm}
        setUserForm={setUserForm}
        userFormError={userFormError}
        onSaveUser={handleSaveUser}
        confirmSaveUserId={confirmSaveUserId}
        selectedUser={selectedUser}
      />

      <PricingModal
        show={showPricingModal}
        onHide={() => setShowPricingModal(false)}
        selectedProduct={selectedProduct}
        pricingForm={pricingForm}
        setPricingForm={setPricingForm}
        pricingError={pricingError}
        onSavePricing={handleSavePricing}
        onUpdateOptionChoiceCost={handleUpdateOptionChoiceCost}
        onAddOptionChoice={handleAddOptionChoice}
        onDeleteOptionChoice={handleDeleteOptionChoice}
      />

      {/* JOB CARD CREATE / EDIT MODAL */}
      <JobCardModal
        show={showJobCardModal}
        onHide={() => setShowJobCardModal(false)}
        isEditingJobCard={isEditingJobCard}
        selectedJobCard={selectedJobCard}
        selectedOrder={selectedOrder}
        jobCardForm={jobCardForm}
        setJobCardForm={setJobCardForm}
        jobCardError={jobCardError}
        onSaveJobCard={handleSaveJobCard}
        confirmSaveJobCardId={confirmSaveJobCardId}
      />

      {/* PRINTABLE JOB TICKET MODAL */}
      {selectedJobCard && (
        <Modal
          show={showTicketModal}
          onHide={() => setShowTicketModal(false)}
          centered
          size="lg"
          className="job-ticket-print-modal"
        >
          <Modal.Header closeButton className="d-print-none">
            <Modal.Title className="fw-bold text-dark">
              Job Card Ticket - {selectedJobCard.jobCardId}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-5" id="printable-job-ticket">
            {/* Styles for printing */}
            <style>
              {`
                @media print {
                  body * {
                    visibility: hidden;
                  }
                  #printable-job-ticket, #printable-job-ticket * {
                    visibility: visible;
                  }
                  #printable-job-ticket {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                    border: none !important;
                    box-shadow: none !important;
                    margin: 0 !important;
                    padding: 0 !important;
                  }
                  .d-print-none {
                    display: none !important;
                  }
                }
              `}
            </style>

            <div className="border border-dark p-4 rounded bg-white shadow-sm position-relative">
              {/* Corner priority stamp */}
              <div
                className="position-absolute top-0 end-0 m-3 px-3 py-1 border border-2 fw-bold text-uppercase rounded"
                style={{
                  color: selectedJobCard.priority === 'urgent' ? 'red' : selectedJobCard.priority === 'high' ? 'orange' : '#777',
                  borderColor: selectedJobCard.priority === 'urgent' ? 'red' : selectedJobCard.priority === 'high' ? 'orange' : '#777',
                  transform: 'rotate(5deg)',
                  fontSize: '14px'
                }}
              >
                {selectedJobCard.priority} Priority
              </div>

              {/* Title Header */}
              <div className="text-center border-bottom pb-4 mb-4">
                <h2 className="fw-bold text-dark mb-1">PRATHITHA DIGITAL PRINTERS</h2>
                <h4 className="fw-bold text-secondary mb-1">PRODUCTION JOB TICKET</h4>
                <p className="text-muted small mb-0">Chromepet, Chennai | Support: +91 91508 08130</p>
              </div>

              {/* Job Info Grid */}
              <Row className="g-3 mb-4">
                <Col xs={6}>
                  <div style={{ fontSize: '13px' }}><strong>Job Ticket ID:</strong> <span className="font-monospace fw-bold">{selectedJobCard.jobCardId}</span></div>
                  <div style={{ fontSize: '13px' }} className="mt-1"><strong>Order ID Ref:</strong> <span className="font-monospace fw-bold">{selectedJobCard.orderId}</span></div>
                  <div style={{ fontSize: '13px' }} className="mt-1"><strong>Date Generated:</strong> {new Date(Number(selectedJobCard.createdAt) ? Number(selectedJobCard.createdAt) : selectedJobCard.createdAt).toLocaleString()}</div>
                </Col>
                <Col xs={6}>
                  <div style={{ fontSize: '13px' }}><strong>Customer Name:</strong> {selectedJobCard.customerName}</div>
                  {selectedJobCard.customerId && <div style={{ fontSize: '13px' }}><strong>Customer ID:</strong> <span className="font-monospace text-muted">{selectedJobCard.customerId}</span></div>}
                  <div style={{ fontSize: '13px' }}><strong>Customer Phone:</strong> {selectedJobCard.customerPhone}</div>
                  <div style={{ fontSize: '13px' }} className="mt-1"><strong>Job Status:</strong> <span className="text-capitalize fw-bold text-dark">{selectedJobCard.status.replace('_', ' ')}</span></div>
                </Col>
              </Row>

              {/* Assignments */}
              <h5 className="fw-bold text-dark border-bottom pb-1.5 mb-3" style={{ fontSize: '14px' }}>PRODUCTION ASSIGNMENTS</h5>
              <Row className="g-0 mb-4 border rounded">
                <Col xs={3} className="text-center border-end p-2 bg-light">
                  <span className="text-muted d-block" style={{ fontSize: '11px' }}>Operator</span>
                  <strong className="text-dark" style={{ fontSize: '13px' }}>{selectedJobCard.operator || 'Not Assigned'}</strong>
                </Col>
                <Col xs={3} className="text-center border-end p-2 bg-light">
                  <span className="text-muted d-block" style={{ fontSize: '11px' }}>Designer</span>
                  <strong className="text-dark" style={{ fontSize: '13px' }}>{selectedJobCard.designer || 'Not Assigned'}</strong>
                </Col>
                <Col xs={3} className="text-center border-end p-2 bg-light">
                  <span className="text-muted d-block" style={{ fontSize: '11px' }}>Machine</span>
                  <strong className="text-dark" style={{ fontSize: '13px' }}>{selectedJobCard.machine || 'Not Assigned'}</strong>
                </Col>
                <Col xs={3} className="text-center p-2 bg-light">
                  <span className="text-muted d-block" style={{ fontSize: '11px' }}>Material</span>
                  <strong className="text-dark" style={{ fontSize: '13px' }}>{selectedJobCard.material || 'Not Specified'}</strong>
                </Col>
              </Row>

              {/* Items Details */}
              <h5 className="fw-bold text-dark border-bottom pb-1.5 mb-3" style={{ fontSize: '14px' }}>ITEMS TO PRINT</h5>
              <Table bordered size="sm" className="mb-4">
                <thead className="table-light">
                  <tr style={{ fontSize: '12px' }}>
                    <th className="py-2 px-3">Product Name & Specifications</th>
                    <th className="py-2 text-center" style={{ width: '100px' }}>Quantity</th>
                    <th className="py-2 text-end px-3" style={{ width: '180px' }}>File Name</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedJobCard.items && selectedJobCard.items.map((item: any, idx: number) => (
                    <tr key={idx} style={{ fontSize: '13px' }}>
                      <td className="py-2 px-3">
                        <strong className="text-dark">{item.productName}</strong>
                        <div className="text-muted font-monospace mt-1" style={{ fontSize: '10.5px' }}>
                          {item.options && item.options.map((opt: any) => (
                            <div key={opt.key} className="text-capitalize">
                              {opt.key.replace(/([A-Z])/g, ' $1')}: <span className="fw-bold text-dark">{opt.value}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 text-center align-middle fw-bold text-dark">{item.quantity}</td>
                      <td className="py-2 text-end align-middle px-3 font-monospace text-muted" style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                        {item.designFileName ? formatDesignFileName(item.designFileName) : 'No Design Uploaded'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Instructions */}
              <Row className="g-3 mb-4">
                <Col xs={6}>
                  <div className="bg-light p-3 rounded border h-100" style={{ fontSize: '12.5px' }}>
                    <strong className="text-dark d-block mb-1.5"><i className="fas fa-cut me-1"></i>Finishing Instructions:</strong>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }} className="text-muted">
                      {selectedJobCard.finishingInstructions || 'Standard cutting and packaging.'}
                    </div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="bg-light p-3 rounded border h-100" style={{ fontSize: '12.5px' }}>
                    <strong className="text-dark d-block mb-1.5"><i className="far fa-sticky-note me-1"></i>Special Job Notes:</strong>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }} className="text-muted">
                      {selectedJobCard.notes || 'No extra notes.'}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Quality Control Checklist */}
              <h5 className="fw-bold text-dark border-bottom pb-1.5 mb-3" style={{ fontSize: '14px' }}>QUALITY CONTROL CHECKLIST</h5>
              <Row className="g-2 mb-4 px-2" style={{ fontSize: '12.5px' }}>
                <Col xs={6} md={3}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="border border-dark rounded-circle" style={{ width: '16px', height: '16px', minWidth: '16px' }}></div>
                    <span>Resolution & Text Check</span>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="border border-dark rounded-circle" style={{ width: '16px', height: '16px', minWidth: '16px' }}></div>
                    <span>Color Profile Correct</span>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="border border-dark rounded-circle" style={{ width: '16px', height: '16px', minWidth: '16px' }}></div>
                    <span>Cutting & Size Match</span>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="border border-dark rounded-circle" style={{ width: '16px', height: '16px', minWidth: '16px' }}></div>
                    <span>Quantity Verified</span>
                  </div>
                </Col>
              </Row>

              {/* Signatures */}
              <Row className="g-3 pt-4 border-top text-center mt-5" style={{ fontSize: '12px' }}>
                <Col xs={4}>
                  <div className="border-top border-secondary pt-2 mx-3 text-muted">Operator Sign</div>
                </Col>
                <Col xs={4}>
                  <div className="border-top border-secondary pt-2 mx-3 text-muted">QC Sign-off</div>
                </Col>
                <Col xs={4}>
                  <div className="border-top border-secondary pt-2 mx-3 text-muted">Customer Dispatch Sign</div>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0 d-print-none">
            <Button variant="secondary" onClick={() => setShowTicketModal(false)} style={{ borderRadius: '8px', fontSize: '13px' }}>
              Close
            </Button>
            <Button variant="primary" onClick={() => window.print()} style={{ borderRadius: '8px', fontSize: '13px' }}>
              <i className="fas fa-print me-1"></i> Send to Printer
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* INVENTORY CREATE / EDIT MODAL */}
      <Modal show={showInventoryModal} onHide={() => setShowInventoryModal(false)} centered backdrop="static">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-dark">{isEditingInventory ? 'Edit Inventory Item' : 'Add New Inventory Item'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveInventory}>
          <Modal.Body className="py-4">
            {inventoryFormError && <Alert variant="danger" className="py-2.5 rounded-3 border-0 mb-3">{inventoryFormError}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Item Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Star Flex Banner Media 340gsm"
                value={inventoryForm.name}
                onChange={(e) => setInventoryForm({ ...inventoryForm, name: e.target.value })}
                style={{ borderRadius: '8px' }}
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Category</Form.Label>
                  <Form.Select
                    value={inventoryForm.category}
                    onChange={(e) => setInventoryForm({ ...inventoryForm, category: e.target.value })}
                    style={{ borderRadius: '8px' }}
                  >
                    <option value="Banner Printing">Banner Printing</option>
                    <option value="Photo frames printing">Photo frames printing</option>
                    <option value="Digital printings">Digital printings</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Unit of Measure</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. rolls, sheets, pcs"
                    value={inventoryForm.unit}
                    onChange={(e) => setInventoryForm({ ...inventoryForm, unit: e.target.value })}
                    style={{ borderRadius: '8px' }}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {inventoryForm.category === 'Banner Printing' && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Banner Type</Form.Label>
                      <Form.Select
                        value={inventoryForm.bannerType}
                        onChange={(e) => setInventoryForm({ ...inventoryForm, bannerType: e.target.value })}
                        style={{ borderRadius: '8px' }}
                      >
                        <option value="">-- Select Type --</option>
                        <option value="Standard Flex">Standard Flex (280gsm)</option>
                        <option value="Star Flex">Star Flex (Premium)</option>
                        <option value="Backlit Flex">Backlit Flex</option>
                        <option value="Blackout Flex">Blackout Flex</option>
                        <option value="Fabric Banner">Fabric Banner</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Banner Roll Size</Form.Label>
                      <Form.Select
                        value={inventoryForm.bannerSize}
                        onChange={(e) => setInventoryForm({ ...inventoryForm, bannerSize: e.target.value })}
                        style={{ borderRadius: '8px' }}
                      >
                        <option value="">-- Select Size --</option>
                        <option value="3.2m x 50m">3.2m x 50m (10ft roll)</option>
                        <option value="2.5m x 50m">2.5m x 50m (8ft roll)</option>
                        <option value="1.8m x 50m">1.8m x 50m (6ft roll)</option>
                        <option value="1.2m x 50m">1.2m x 50m (4ft roll)</option>
                        <option value="6ft x 3ft">6ft x 3ft (Standard Standee)</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Overall Roll Length (meters)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="e.g. 70"
                        value={inventoryForm.overallLength}
                        onChange={(e) => setInventoryForm({ ...inventoryForm, overallLength: Number(e.target.value) })}
                        style={{ borderRadius: '8px' }}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Roll Length Limit (meters)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="e.g. 10"
                        value={inventoryForm.rollLengthLimit}
                        onChange={(e) => setInventoryForm({ ...inventoryForm, rollLengthLimit: Number(e.target.value) })}
                        style={{ borderRadius: '8px' }}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Current Stock Level</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="0"
                    value={inventoryForm.stock}
                    onChange={(e) => setInventoryForm({ ...inventoryForm, stock: Number(e.target.value) })}
                    style={{ borderRadius: '8px' }}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Minimum Stock Alert Level</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="10"
                    value={inventoryForm.minStock}
                    onChange={(e) => setInventoryForm({ ...inventoryForm, minStock: Number(e.target.value) })}
                    style={{ borderRadius: '8px' }}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Supplier Details</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Classic Media Corp"
                value={inventoryForm.supplier}
                onChange={(e) => setInventoryForm({ ...inventoryForm, supplier: e.target.value })}
                style={{ borderRadius: '8px' }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Order Date</Form.Label>
              <Form.Control
                type="date"
                value={inventoryForm.orderDate}
                onChange={(e) => setInventoryForm({ ...inventoryForm, orderDate: e.target.value })}
                style={{ borderRadius: '8px' }}
              />
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '12px' }}>Notes / Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Storage instructions, quality checks..."
                value={inventoryForm.notes}
                onChange={(e) => setInventoryForm({ ...inventoryForm, notes: e.target.value })}
                style={{ borderRadius: '8px' }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setShowInventoryModal(false)} className="fw-semibold" style={{ borderRadius: '8px' }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="fw-semibold px-4" style={{ borderRadius: '8px' }}>
              {isEditingInventory ? 'Save Changes' : 'Add Item'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ATTENDANCE SUMMARY MODAL */}
      {selectedAttendanceUser && (
        <Modal
          show={showAttendanceSummaryModal}
          onHide={() => {
            setShowAttendanceSummaryModal(false);
            setSelectedAttendanceUser(null);
          }}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold text-dark">
              Attendance History: {selectedAttendanceUser.username}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="bg-light p-3 rounded-3 mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <Form.Label className="mb-0 fw-semibold text-muted" style={{ fontSize: '13px' }}>Start Date:</Form.Label>
                <Form.Control
                  type="date"
                  value={attendanceSummaryStart}
                  onChange={(e) => setAttendanceSummaryStart(e.target.value)}
                  style={{ width: '150px', borderRadius: '8px', fontSize: '13px' }}
                />
                <Form.Label className="mb-0 fw-semibold text-muted ms-2" style={{ fontSize: '13px' }}>End Date:</Form.Label>
                <Form.Control
                  type="date"
                  value={attendanceSummaryEnd}
                  onChange={(e) => setAttendanceSummaryEnd(e.target.value)}
                  style={{ width: '150px', borderRadius: '8px', fontSize: '13px' }}
                />
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                className="fw-semibold"
                style={{ borderRadius: '8px' }}
                onClick={() => refetchSummary()}
                disabled={summaryLoading}
              >
                <i className="fas fa-sync-alt me-1"></i> Reload
              </Button>
            </div>

            {summaryLoading ? (
              <div className="text-center py-4 text-muted">
                <Spinner animation="border" variant="primary" className="mb-2" />
                <p>Calculating summary...</p>
              </div>
            ) : (
              <>
                {/* Stats cards */}
                <Row className="g-3 mb-4">
                  <Col xs={6} md={3}>
                    <Card className="border-0 bg-success bg-opacity-10 text-success text-center py-3 rounded-3">
                      <h3 className="fw-bold mb-1">{summaryData?.attendanceSummary?.presentCount || 0}</h3>
                      <div className="text-muted fw-semibold" style={{ fontSize: '12px' }}>Days Present</div>
                    </Card>
                  </Col>
                  <Col xs={6} md={3}>
                    <Card className="border-0 bg-danger bg-opacity-10 text-danger text-center py-3 rounded-3">
                      <h3 className="fw-bold mb-1">{summaryData?.attendanceSummary?.absentCount || 0}</h3>
                      <div className="text-muted fw-semibold" style={{ fontSize: '12px' }}>Days Absent</div>
                    </Card>
                  </Col>
                  <Col xs={6} md={3}>
                    <Card className="border-0 bg-warning bg-opacity-10 text-warning-emphasis text-center py-3 rounded-3">
                      <h3 className="fw-bold mb-1">{summaryData?.attendanceSummary?.halfDayCount || 0}</h3>
                      <div className="text-muted fw-semibold" style={{ fontSize: '12px' }}>Half Days</div>
                    </Card>
                  </Col>
                  <Col xs={6} md={3}>
                    <Card className="border-0 bg-info bg-opacity-10 text-info text-center py-3 rounded-3">
                      <h3 className="fw-bold mb-1">{summaryData?.attendanceSummary?.leaveCount || 0}</h3>
                      <div className="text-muted fw-semibold" style={{ fontSize: '12px' }}>Leaves</div>
                    </Card>
                  </Col>
                </Row>

                {/* History Table */}
                <h5 className="fw-bold text-dark mb-3">Logs Details</h5>
                {(summaryData?.attendanceSummary?.records || []).length === 0 ? (
                  <Alert variant="info" className="text-center py-3 border-0">
                    No attendance records found for this period.
                  </Alert>
                ) : (
                  <div className="table-responsive" style={{ maxHeight: '300px' }}>
                    <Table hover size="sm" className="align-middle mb-0">
                      <thead className="table-light">
                        <tr style={{ fontSize: '12px', color: '#64748b' }}>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Timing</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(summaryData?.attendanceSummary?.records || []).map((rec: any) => {
                          let badgeBg = 'success';
                          if (rec.status === 'absent') badgeBg = 'danger';
                          else if (rec.status === 'half_day') badgeBg = 'warning';
                          else if (rec.status === 'leave') badgeBg = 'info';

                          return (
                            <tr key={rec.id} style={{ fontSize: '12.5px' }}>
                              <td className="py-2">{rec.date}</td>
                              <td>
                                <Badge bg={badgeBg} className="text-capitalize">{rec.status.replace('_', ' ')}</Badge>
                              </td>
                              <td>
                                {rec.status === 'present' || rec.status === 'half_day' ? (
                                  <span>{rec.checkIn || '--:--'} to {rec.checkOut || '--:--'}</span>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="text-muted">{rec.notes || <span className="text-muted-light">—</span>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAttendanceSummaryModal(false);
                setSelectedAttendanceUser(null);
              }}
              style={{ borderRadius: '8px', fontSize: '13px' }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* PRODUCT CATALOGUE MODAL */}
      <ProductModal
        show={showProductModal}
        onHide={() => {
          setShowProductModal(false);
          setEditingProduct(null);
        }}
        onSuccess={(msg) => {
          triggerToast(msg, 'success');
          refetchProducts();
        }}
        product={editingProduct}
      />

      {/* PAYSLIP GENERATION MODAL */}
      <PayslipModal
        show={showPayslipModal}
        onHide={() => setShowPayslipModal(false)}
        onSuccess={(msg) => {
          triggerToast(msg, 'success');
          refetchPayslips();
        }}
        users={usersData?.users || []}
      />

      {/* CUSTOMER PROFILE MODAL */}
      <CustomerModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        isEditing={isEditingCustomer}
        selectedCustomer={selectedCustomer}
        onCustomerSaved={(savedCustomer) => {
          triggerToast(
            isEditingCustomer
              ? 'Customer profile updated successfully!'
              : 'Customer profile created successfully!',
            'success'
          );
          refetchUsers();
        }}
      />

      {/* Toast Notification Container */}
      <ToastContainer position="top-end" className="p-3 position-fixed" style={{ zIndex: 9999 }}>
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3500}
          autohide
          bg={toastVariant}
          className="text-white border-0 shadow-lg rounded-3"
        >
          <Toast.Header closeButton={false} className={`bg-${toastVariant} text-white border-0 rounded-top-3 d-flex align-items-center justify-content-between`}>
            <div className="d-flex align-items-center gap-2">
              <i className={
                toastVariant === 'success' ? 'fas fa-check-circle fs-5' :
                  toastVariant === 'danger' ? 'fas fa-times-circle fs-5' :
                    toastVariant === 'warning' ? 'fas fa-exclamation-triangle fs-5' :
                      'fas fa-info-circle fs-5'
              }></i>
              <strong className="text-capitalize">
                {toastVariant === 'success' ? 'Success' :
                  toastVariant === 'danger' ? 'Error' :
                    toastVariant === 'warning' ? 'Warning' :
                      'Info'}
              </strong>
            </div>
            <div className="d-flex align-items-center gap-2">
              <small className="text-white-50">Just now</small>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => setShowToast(false)}
                aria-label="Close"
              ></button>
            </div>
          </Toast.Header>
          <Toast.Body className="py-2.5 px-3 fs-6 bg-white text-dark rounded-bottom-3 border border-top-0 border-light-subtle">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <Footer />
    </div>
  );
};

export default AdminDashboard;

interface AttendanceRowProps {
  user: any;
  initialAttendance: any;
  date: string;
  onSave: (userId: string, date: string, status: string, checkIn: string, checkOut: string, notes: string) => Promise<void>;
  onViewSummary: (user: any) => void;
}

const AttendanceRow: React.FC<AttendanceRowProps> = ({ user, initialAttendance, date, onSave, onViewSummary }) => {
  const [status, setStatus] = useState(initialAttendance.status || 'present');
  const [checkIn, setCheckIn] = useState(initialAttendance.checkIn || '');
  const [checkOut, setCheckOut] = useState(initialAttendance.checkOut || '');
  const [notes, setNotes] = useState(initialAttendance.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setStatus(initialAttendance.status || 'present');
    setCheckIn(initialAttendance.checkIn || '');
    setCheckOut(initialAttendance.checkOut || '');
    setNotes(initialAttendance.notes || '');
  }, [initialAttendance]);

  const handleSaveRow = async () => {
    setIsSaving(true);
    await onSave(user.id, date, status, checkIn, checkOut, notes);
    setIsSaving(false);
  };

  const getStatusClass = (s: string) => {
    switch (s) {
      case 'present': return 'bg-success text-white';
      case 'absent': return 'bg-danger text-white';
      case 'half_day': return 'bg-warning text-dark';
      case 'leave': return 'bg-info text-white';
      default: return 'bg-secondary text-white';
    }
  };

  return (
    <tr className="bg-light-row" style={{ fontSize: '13px' }}>
      <td className="fw-semibold text-dark py-3">
        {user.username}
      </td>
      <td className="text-muted text-capitalize">
        {user.role.replace('_', ' ')}
      </td>
      <td>
        <Form.Select
          size="sm"
          value={status}
          onChange={(e) => {
            const nextStatus = e.target.value;
            setStatus(nextStatus);
            if (nextStatus === 'present' && !checkIn) {
              setCheckIn('09:00');
              setCheckOut('18:00');
            } else if ((nextStatus === 'absent' || nextStatus === 'leave')) {
              setCheckIn('');
              setCheckOut('');
            }
          }}
          className={`fw-semibold rounded-pill px-2.5 py-1 ${getStatusClass(status)}`}
          style={{ width: '130px', border: 'none', cursor: 'pointer' }}
        >
          <option value="present" className="bg-white text-dark">Present</option>
          <option value="absent" className="bg-white text-dark">Absent</option>
          <option value="half_day" className="bg-white text-dark">Half Day</option>
          <option value="leave" className="bg-white text-dark">Leave</option>
        </Form.Select>
      </td>
      <td>
        <Form.Control
          type="time"
          size="sm"
          value={checkIn}
          onChange={(e) => setCheckIn(e.target.value)}
          disabled={status === 'absent' || status === 'leave'}
          style={{ width: '110px', borderRadius: '6px' }}
        />
      </td>
      <td>
        <Form.Control
          type="time"
          size="sm"
          value={checkOut}
          onChange={(e) => setCheckOut(e.target.value)}
          disabled={status === 'absent' || status === 'leave'}
          style={{ width: '110px', borderRadius: '6px' }}
        />
      </td>
      <td>
        {initialAttendance.location ? (
          initialAttendance.latitude && initialAttendance.longitude ? (
            <a
              href={`https://www.google.com/maps?q=${initialAttendance.latitude},${initialAttendance.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="d-inline-flex align-items-center gap-1 text-primary text-decoration-none fw-semibold"
              style={{ fontSize: '12px' }}
              title="View Check-In Location on Google Maps"
            >
              <i className="fas fa-map-marker-alt text-danger"></i>
              <span>{initialAttendance.location}</span>
              <i className="fas fa-external-link-alt ms-1" style={{ fontSize: '10px' }}></i>
            </a>
          ) : (
            <span className="text-muted d-inline-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
              <i className="fas fa-map-marker-alt text-secondary"></i>
              <span>{initialAttendance.location}</span>
            </span>
          )
        ) : (
          <span className="text-muted" style={{ fontSize: '12px' }}>-</span>
        )}
      </td>
      <td>
        <Form.Control
          type="text"
          size="sm"
          placeholder="Remarks..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ borderRadius: '6px', minWidth: '150px' }}
        />
      </td>
      <td className="text-end">
        <div className="d-flex align-items-center justify-content-end gap-2">
          <Button
            variant="primary"
            size="sm"
            style={{ borderRadius: '6px', minWidth: '60px' }}
            onClick={handleSaveRow}
            disabled={isSaving}
          >
            {isSaving ? <Spinner size="sm" animation="border" /> : 'Save'}
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            style={{ borderRadius: '6px' }}
            onClick={() => onViewSummary(user)}
            title="View Attendance History"
          >
            <i className="fas fa-history"></i>
          </Button>
        </div>
      </td>
    </tr>
  );
};
